import './pages.css';
import Prism from 'prismjs';
import 'prismjs/themes/prism-tomorrow.css';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-bash';
import React, { useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import config from '../Config';
import developerAccessImg from './images/developer-access.png';
import clusterPickerImg from './images/cluster-picker.png';
import clusterPickerDropdownImg from './images/cluster-picker-dropdown.png';
import architectureVpcImg from './images/architecture-vpc.svg';
import architectureAutoscaleImg from './images/architecture-autoscale.svg';

/* ─────────────────────────────────────────────
   Sidebar nav structure
───────────────────────────────────────────── */
const NAV = [
  {
    group: 'Getting Started',
    items: [
      { id: 'overview',    label: 'Overview' },
      { id: 'quickstart',  label: 'Quick Start' },
    ],
  },
  {
    group: 'Agent Skill',
    items: [
      { id: 'agent-skill', label: 'Cursor / Claude Skill' },
    ],
  },
  {
    group: 'Cluster',
    items: [
      { id: 'cluster-overview',   label: 'Overview' },
      { id: 'cluster-switching',  label: 'Switching Clusters' },
      { id: 'cluster-create',     label: 'Creating a Cluster' },
      { id: 'cluster-status',     label: 'Status Reference' },
      { id: 'cluster-billing',    label: 'Billing' },
      { id: 'cluster-recovery',   label: 'Recovery' },
    ],
  },
  {
    group: 'Dedicated API',
    items: [
      { id: 'job-ui',   label: 'Jobs' },
      { id: 'cron',     label: 'Cron' },
      { id: 'api',      label: 'API Guide' },
      { id: 'examples', label: 'Use Cases' },
      { id: 'docker',   label: 'Sample Code' },
    ],
  },
  {
    group: 'Container Registry',
    items: [
      { id: 'mcr',      label: 'Overview & UI Guide' },
      { id: 'mcrpush',  label: 'Build & Push' },
    ],
  },
  {
    group: 'Automations',
    items: [
      { id: 'cdci-overview', label: 'Overview & UI Guide' },
      { id: 'cdci-github',   label: 'GitHub Integration' },
      { id: 'cdci-pipeline', label: 'Pipeline Flow' },
    ],
  },
  {
    group: 'Deployments',
    items: [
      { id: 'deploy-overview',  label: 'Overview & UI Guide' },
      { id: 'deploy-guide',     label: 'Step-by-Step Guide' },
      { id: 'deploy-configmap', label: 'ConfigMaps' },
      { id: 'deploy-secrets',   label: 'Secrets' },
      { id: 'deploy-domains',   label: 'Domains' },
      { id: 'deploy-host',      label: 'Custom Hosts' },
    ],
  },
  {
    group: 'Platform',
    items: [
      { id: 'architecture',   label: 'Architecture' },
      { id: 'vpc-isolation',  label: 'Network Isolation (VPC)' },
      { id: 'benefits',       label: 'Why Mermadic' },
      { id: 'faq',            label: 'FAQ' },
    ],
  },
  {
    group: 'API Reference',
    items: [
      { id: 'api-auth',        label: 'Authentication' },
      { id: 'api-jobs',        label: 'Dedicated API' },
      { id: 'api-registry',    label: 'Container Registry' },
      { id: 'api-deployments', label: 'Deployments' },
      { id: 'api-domains',     label: 'Domains' },
    ],
  },
];

/* ─────────────────────────────────────────────
   Code block helper
───────────────────────────────────────────── */
const Code = ({ lang = 'bash', children }) => (
  <div className="docs-code">
    {lang && <span className="docs-code-lang">{lang}</span>}
    <pre><code className={`language-${lang}`}>{children}</code></pre>
  </div>
);

const P = ({ children }) => <p className="docs-p">{children}</p>;
const H2 = ({ children }) => <h2 className="docs-h2">{children}</h2>;
const H3 = ({ children }) => <h3 className="docs-h3">{children}</h3>;
const H4 = ({ children }) => <h4 className="docs-h4">{children}</h4>;
const IC = ({ children }) => <code className="docs-ic">{children}</code>;
const Note = ({ children }) => <div className="docs-note">{children}</div>;
const UiAccess = ({ children }) => <div className="docs-ui-access"><span className="docs-ui-access-label">🖥 Accessing in the UI</span>{children}</div>;
const Tag = ({ method }) => <span className={`docs-tag docs-tag--${method.toLowerCase()}`}>{method}</span>;

const EndpointRow = ({ method, path, desc }) => (
  <div className="docs-endpoint">
    <Tag method={method} />
    <code className="docs-endpoint-path">{path}</code>
    <span className="docs-endpoint-desc">{desc}</span>
  </div>
);

const SKILL_DOWNLOAD_URL = `${config.MASTER_API}/v1/skill/download`;

const DownloadSkillButton = () => (
  <a className="docs-download-btn" href={SKILL_DOWNLOAD_URL} download>
    ⬇ Download the Skill (.zip)
  </a>
);

/* ─────────────────────────────────────────────
   Section content definitions
───────────────────────────────────────────── */
const SECTIONS = {
  'agent-skill': (
    <>
      <H2>Agent Skill for Cursor &amp; Claude Code</H2>
      <P>The Mermadic Agent Skill lets Cursor and Claude Code operate your Mermadic cluster through the dedicated API directly from the chat: authenticate with an API key, pick a cluster, and run jobs, deployments, ConfigMaps, cron schedules, container builds, GitHub automation, custom domains, and cluster recovery — all in plain English.</P>
      <P>It also answers <strong>questions about the Mermadic web UI</strong> — it bundles a guide to the dashboard, so you can ask how a feature works or where to find something (e.g. "In the UI, what does 'create from existing app' mean in an automation pipeline?") and get an answer without leaving your editor.</P>
      <P>The same folder installs unchanged into either editor. Your API key is held by a small bundled helper and stored in your OS credential store; it never enters the agent's context, logs, or command arguments.</P>

      <div className="docs-download-wrap">
        <DownloadSkillButton />
        <span className="docs-download-hint">Unzip it, then run the installer for your platform below.</span>
      </div>

      <H3>Prerequisites</H3>
      <ul className="docs-list">
        <li><strong>Node.js 14+</strong> — the helper uses only Node's built-in HTTP modules.</li>
        <li>A Mermadic <strong>API key</strong> generated in the web app under <strong>Developer Access</strong> (a long-lived <IC>mk_...</IC> key).</li>
        <li>For deployment / domain features: a dedicated cluster (the shared cluster rejects those operations). ConfigMaps work on every tier.</li>
      </ul>

      <H3>Get your API key</H3>
      <P>The skill authenticates with a Mermadic API key. Generate one from your account dashboard — you'll paste it once during login (see First run below):</P>
      <ul className="docs-list">
        <li>Log in to the web app and open <strong>My Account</strong> (top-right button).</li>
        <li>Click <strong>Developer Access</strong> in the left sidebar.</li>
        <li>Enter a key name (e.g. <IC>cursor-skill</IC>) and click <strong>Generate Key</strong>.</li>
        <li>Copy the <IC>mk_...</IC> key and store it securely — it is shown only once. One key works for every cluster you own.</li>
      </ul>
      <div className="docs-screenshot-wrapper">
        <img
          src={developerAccessImg}
          alt="Developer Access page — generate an API key"
          className="docs-screenshot"
        />
        <p className="docs-screenshot-caption">My Account → Developer Access → Generate Key</p>
      </div>
      <Note>You never paste the key into the chat — the bundled helper reads it during <IC>auth login</IC> and stores it in your OS credential store. You can revoke or rotate keys anytime from the Developer Access page.</Note>

      <H3>Install</H3>
      <P>Unzip the download, open a terminal in the unzipped folder, and run the installer for your editor.</P>

      <H4>Windows (recommended)</H4>
      <P>Use the <IC>.cmd</IC> launcher — it works even if PowerShell blocks scripts ("running scripts is disabled on this system"). In PowerShell you must prefix it with <IC>.\</IC>. Usage: <IC>install.cmd [platform] [scope]</IC> — both arguments are optional and default to <IC>cursor project</IC>.</P>
      <Code lang="bash">{`# Cursor, current project
.\\install.cmd cursor project

# Claude Code, all projects (user scope)
.\\install.cmd claude user`}</Code>
      <P>Prefer PowerShell directly? Either allow local scripts once (<IC>Set-ExecutionPolicy -Scope CurrentUser RemoteSigned</IC>) or bypass for a single run:</P>
      <Code lang="bash">{`powershell -ExecutionPolicy Bypass -File .\\install.ps1 -Platform cursor -Scope project`}</Code>

      <H4>macOS / Linux</H4>
      <Code lang="bash">{`chmod +x install.sh
./install.sh --platform cursor --scope project
./install.sh --platform claude --scope user`}</Code>

      <P>The installer copies the skill into <IC>.cursor/skills/mermadic</IC> (or <IC>.claude/skills/mermadic</IC>) and installs its Node dependencies so the folder is self-contained.</P>

      <H3>First run</H3>
      <P>Generate an API key in the web app (<strong>Developer Access</strong>), then log in once. The key is saved to your OS credential store and reused automatically:</P>
      <Code lang="bash">{`node ".cursor/skills/mermadic/scripts/mermadic.js" auth login`}</Code>
      <P>After that, just talk to the agent. It reads the skill automatically when your request mentions Mermadic.</P>

      <H3>Example prompts</H3>
      <ul className="docs-list">
        <li>"Run <IC>web-test.zip</IC> as a one-off job on my dedicated cluster."</li>
        <li>"Deploy this app with 3 replicas and the <IC>webtest.env</IC> ConfigMap."</li>
        <li>"Build a container image from this zip and push it to my registry."</li>
        <li>"Show me the logs for my last failed job."</li>
        <li>"Create a cron job that runs every night at 2am."</li>
        <li>"In the web UI, how do I set a custom domain for a deployment?"</li>
      </ul>

      <Note>Before each action the skill summarizes exactly which requests it will send (with your parameters) and logs every action locally for transparency. Managing GitHub App access remains a web-UI-only action.</Note>
    </>
  ),
  overview: (
    <>
      <H2>Overview</H2>
      <P>Mermadic is an application platform that runs your containers and hosts your services — without a DevOps team. Push a container, and Mermadic handles the cluster, scaling, networking, TLS, and registry for you. Every account runs on <strong>isolated infrastructure</strong>: your own private VPC, your own cluster, your own nodes, and a dedicated API per cluster — all managed from a simple dashboard and REST API.</P>

      <H3>Key Features</H3>
      <ul className="docs-feature-grid">
        {[
          { title: 'Isolated Private VPC', dedicated: true,
            desc: <>Your clusters run inside a private AWS VPC provisioned exclusively for your account — no other customer's traffic can reach your subnets.</> },
          { title: 'Isolated Infrastructure', dedicated: true,
            desc: <>Your own cluster on your own nodes — isolation at the infrastructure layer, not a shared cluster carved up by namespaces.</> },
          { title: 'Dedicated API Per Cluster', dedicated: true,
            desc: <>Each cluster gets its own dedicated API endpoint — no shared request queues or cross-tenant rate limits.</> },
          { title: 'Simple REST API',
            desc: <>Submit jobs and manage deployments via API — no infrastructure expertise required. Find your cluster's base URL and an interactive reference under <Link to="/account?tab=api">My Account → API docs</Link>.</> },
          { title: 'Private Container Registry',
            desc: <>Built-in Mermadic Container Registry (MCR) shared across your account, with zero-credential access from your clusters.</> },
          { title: 'ConfigMap Management',
            desc: <>Store environment variables as ConfigMaps and inject them into jobs and deployments at runtime — no image rebuild needed. Available on every tier (stored as an env file on the shared cluster).</> },
          { title: 'Encrypted Secrets',
            desc: <>Keep API keys, passwords, and tokens in a Secret instead of a ConfigMap: encrypted at rest, injected as environment variables at run time, and write-only — no endpoint ever returns a value. Available on every tier.</> },
          { title: 'Elastic Auto-Scaling',
            desc: <>Scales automatically based on workload; idle nodes scale down to save cost.</> },
          { title: 'Long-Lived Deployments', dedicated: true,
            desc: <>Deploy APIs, web apps, and background workers with HTTPS URLs and live monitoring.</> },
          { title: 'CDCI Automation', dedicated: true,
            desc: <>Connect a GitHub branch — every push automatically builds, pushes, and redeploys your app.</> },
          { title: 'Custom Domains', dedicated: true,
            desc: <>Add your domain, create the CNAME record at your DNS provider to verify ownership, then use it as the host for any deployment.</> },
        ].map(({ title, desc, dedicated }) => (
          <li key={title} className={`docs-feature-card${dedicated ? ' docs-feature-card--dedicated' : ''}`}>
            {dedicated && <span className="docs-feature-badge">Dedicated cluster</span>}
            <strong>{title}</strong>
            <span>{desc}</span>
          </li>
        ))}
      </ul>
      <Note>Features marked <span className="docs-feature-badge docs-feature-badge--inline">Dedicated cluster</span> require a Gold or Premium cluster. The free <strong>Shared</strong> tier supports job runs and image builds on Mermadic's shared infrastructure.</Note>

      <H3>An Application Platform — Not a Kubernetes Project</H3>
      <P>Mermadic is built for teams who want to ship applications, not run infrastructure. You get the isolation and control of dedicated infrastructure with the simplicity of a managed platform.</P>
      <ul className="docs-list">
        {[
          ['No DevOps required', 'No cluster setup, no YAML, no node management, no upgrades to babysit. Push a container and it runs.'],
          ['Enterprise-grade isolation', 'Your own private VPC, cluster, and nodes — real infrastructure isolation, not namespace partitioning on a shared cluster.'],
          ['Batteries included', 'Container registry, TLS certificates, custom domains, load balancing, and CI/CD automation are built in — nothing to wire up.'],
          ['Run anything in a container', 'Jobs and batch tasks, long-running APIs and web apps, background workers, scheduled work, ML inference, ETL — if it runs in a container, it runs on Mermadic.'],
          ['Pay for what you use', 'A fixed cluster fee plus per-second compute usage. No per-request pricing that explodes at scale.'],
        ].map(([t, d]) => <li key={t}><strong>{t}</strong> — {d}</li>)}
      </ul>
    </>
  ),

  quickstart: (
    <>
      <H2>Quick Start</H2>
      <P>Get your first job running in under 5 minutes.</P>

      <H3>1. Generate an API Key</H3>
      <P>All API requests are authenticated with an API key. Generate one from your account dashboard:</P>
      <ul className="docs-list">
        <li>Go to <strong>My Account</strong> (top-right button after logging in).</li>
        <li>Click <strong>Developer Access</strong> in the left sidebar.</li>
        <li>Enter a key name (e.g. <IC>CI pipeline</IC>) and click <strong>Generate Key</strong>.</li>
        <li>Copy and store the key securely — it is shown only once.</li>
      </ul>
      <div className="docs-screenshot-wrapper">
        <img
          src={developerAccessImg}
          alt="Developer Access page — generate an API key"
          className="docs-screenshot"
        />
        <p className="docs-screenshot-caption">My Account → Developer Access → Generate Key</p>
      </div>
      <P>Use the API key as a Bearer token in all subsequent requests:</P>
      <Code lang="bash">{`Authorization: Bearer YOUR_API_KEY`}</Code>

      <Note>If you have a <strong>dedicated cluster</strong> (Gold or Premium), your dedicated API runs at your cluster's own URL. Go to <strong>My Account → API docs</strong> to find your cluster-specific base URL and explore the full interactive API reference for your cluster.</Note>

      <H3>2. Submit Your First Job</H3>
      <P>Run a simple Python container from DockerHub. Authenticate with your API key in the <IC>Authorization</IC> header — never in the request body:</P>
      <Code lang="bash">{`curl -X POST https://job.mermadicrunner.com/job-runner/v1/dockerhub/create-job \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "image": "python:3.11",
    "commandArgs": ["python", "-c", "print(\\"Hello from Mermadic!\\")"],
    "resourceRequests": { "cpu": 0.5, "memory": 0.5 }
  }'

# Response (HTTP 201) — the new job id is returned as a JSON string
"20260617143022100250"`}</Code>

      <H3>3. Check Status & Logs</H3>
      <P>The status and log endpoints take <IC>job_id</IC> as a query parameter (still authenticated with your API key header):</P>
      <Code lang="bash">{`# Job details
curl -X POST "https://job.mermadicrunner.com/job-runner/v1/get-job-detail?job_id=20260617143022100250" \\
  -H "Authorization: Bearer YOUR_API_KEY"

# Job logs
curl -X POST "https://job.mermadicrunner.com/job-runner/v1/get-log?job_id=20260617143022100250" \\
  -H "Authorization: Bearer YOUR_API_KEY"`}</Code>

      <Note>You can manage all your API keys from <strong>My Account → Developer Access</strong>. Keys can be revoked at any time. Your API key is always sent in the <IC>Authorization: Bearer</IC> header, never in the request body.</Note>
    </>
  ),

  'job-ui': (
    <>
      <H2>Jobs</H2>
      <P>The Jobs dashboard lets you create, monitor, and manage one-off container jobs from a single three-pane view — no API calls required.</P>

      <UiAccess>
        <ul className="docs-list">
          <li>In the navbar, click <strong>Workloads → <Link to="/log">Jobs</Link></strong>.</li>
          <li>Click <strong>+ New Job</strong> in the top-right to open the job creation modal.</li>
          <li>Select an image source, fill in the details, and click <strong>Create Job</strong>. The new job appears in the left pane immediately.</li>
          <li>Click any job in the left pane to load its status in the middle pane and its log output in the right pane.</li>
        </ul>
      </UiAccess>

      <H3>Three-Pane Layout</H3>

      <H4>Left Pane — Job List</H4>
      <P>Shows all your recent jobs (newest first). Each card displays the <strong>Job ID</strong>, <strong>image name</strong>, <strong>status badge</strong>, and a relative timestamp. Use the filter at the top to view All, Running, or Terminated jobs. The list auto-refreshes every 15 seconds.</P>

      <H4>Middle Pane — Status &amp; Details</H4>
      <P>When a job is selected, the middle pane shows:</P>
      <ul className="docs-list">
        <li>Current <strong>status</strong> with a colored badge (<IC>running</IC> / <IC>Completed</IC> / <IC>Failed</IC> / etc.)</li>
        <li>Image name, start time, finish time, requested resources, and command arguments</li>
        <li>A <strong>Delete</strong> button — only visible for jobs that are currently running. Deleting a job kills only the container process; the pod lingers until its TTL so billing is finalized. The final status becomes <IC>Deleted</IC>.</li>
      </ul>

      <H4>Right Pane — Log</H4>
      <P>Shows the captured log output for the selected job in a terminal-style viewer. A <strong>⬇ Download Log</strong> button saves the complete log as a plain-text file named after the Job ID. Very large logs are shown from the end only, with a notice giving the full size — download it to read the whole thing.</P>

      <H3>Creating a Job via the Modal</H3>
      <P>Click <strong>+ New Job</strong> to open the creation modal. It contains the same fields as the dedicated Create Job page:</P>
      <ul className="docs-list">
        <li><strong>Image Source</strong> — Choose MCR, DockerHub, ECR, or ACR. MCR images require no credentials.</li>
        <li><strong>Image</strong> — For MCR, select from a live dropdown. For other registries, type the full image reference.</li>
        <li><strong>Credentials</strong> — DockerHub private images accept optional credentials; ECR and ACR require them.</li>
        <li><strong>Command Arguments</strong> — Flag name / value pairs (e.g. <IC>--email</IC> → <IC>user@example.com</IC>).</li>
        <li><strong>Resource Requests</strong> — CPU (vCPU) and memory (GB). Defaults apply if left unchecked.</li>
        <li><strong>Environment Variables</strong> — Key/value pairs injected into the container at runtime.</li>
        <li><strong>ConfigMap</strong> — Optional. Injects an existing ConfigMap's keys as env vars. Works on every tier (stored as an env file on the shared cluster).</li>
      </ul>
      <P>On success a banner shows the new Job ID and the list refreshes automatically.</P>

      <H3>Deleting a Running Job</H3>
      <P>Only jobs with a <strong>running</strong> status can be deleted. The Delete button is hidden for completed, failed, or already-deleted jobs. Clicking Delete sends a kill signal to the container process only — the pod remains until its TTL and the sidecar monitor finalizes billing before setting the status to <IC>Deleted</IC>.</P>

      <Note>The API Guide for the Dedicated API is available in the <strong>Dedicated API → API Guide</strong> section of this sidebar if you need to submit or manage jobs programmatically.</Note>
    </>
  ),

  cron: (
    <>
      <H2>Cron Jobs</H2>
      <P>Cron Jobs run a container on a recurring schedule, right inside your cluster. Each scheduled tick creates a normal job — so every run is tracked, billed, and logged exactly like a manually-created job. Cron Jobs are available on <strong>both shared and dedicated clusters</strong>.</P>

      <UiAccess>
        <ul className="docs-list">
          <li>In the navbar, click <strong>Workloads → <Link to="/cron">Cron</Link></strong>.</li>
          <li>Click <strong>+ New Cron Job</strong>, give it a name, pick a schedule, and select an MCR image.</li>
          <li>Select a cron job in the left pane to see its <strong>recent executions</strong> in the middle pane.</li>
          <li>Click any execution to view its <strong>log</strong> in the right pane, and <strong>⬇ Download Log</strong> to save the full output.</li>
          <li>Use <strong>Enabled / Disabled</strong> to pause or resume a schedule, <strong>Edit</strong> to change it, or <strong>Delete</strong> to remove it.</li>
        </ul>
      </UiAccess>

      <H3>MCR Images Only</H3>
      <P>Cron Jobs accept <strong>only images from the Mermadic Container Registry (MCR)</strong>. Because a cron runs unattended on a schedule, Mermadic would otherwise have to store your private registry credentials long-term — so instead we require MCR images, which your cluster is already pre-authorized to pull. <strong>No credentials are ever stored.</strong> Push your image to MCR (see <strong>Container Registry → Build &amp; Push</strong>), then select it when creating the cron job.</P>

      <H3>Schedule Format</H3>
      <P>Schedules use the standard 5-field cron expression: <IC>minute hour day-of-month month day-of-week</IC>. The form includes quick presets, or you can type your own.</P>
      <table className="docs-table">
        <thead><tr><th>Expression</th><th>Runs</th></tr></thead>
        <tbody>
          <tr><td><IC>*/5 * * * *</IC></td><td>Every 5 minutes</td></tr>
          <tr><td><IC>0 * * * *</IC></td><td>Every hour, on the hour</td></tr>
          <tr><td><IC>0 0 * * *</IC></td><td>Every day at midnight</td></tr>
          <tr><td><IC>0 9 * * 1</IC></td><td>Every Monday at 09:00</td></tr>
        </tbody>
      </table>
      <Note>Times follow the <strong>timezone</strong> set on the cron job (defaults to UTC).</Note>

      <H3>Executions &amp; Logs</H3>
      <P>Every time the schedule fires, a new job run is created and appears under that cron job's executions (most recent first). Cron runs are <strong>kept separate from the Job History page</strong> — they only appear under their cron job in the Cron Jobs page, so your manual job history stays uncluttered. Each run's log is captured the same way as any job and remains available after the run finishes, with a <strong>⬇ Download Log</strong> button in the log pane for the complete output.</P>

      <H3>Per-Cluster Scoping</H3>
      <P>Cron jobs belong to the cluster they were created in. When you switch clusters with the picker in the top-right, the Cron Jobs page shows only that cluster's schedules. To configure a cron job on a different cluster, switch to it first.</P>

      <H3>API Endpoints</H3>
      <div className="docs-endpoint-list">
        <EndpointRow method="POST"   path="/job-runner/v1/cron/create"            desc="Create a cron job (MCR image only)" />
        <EndpointRow method="GET"    path="/job-runner/v1/cron/list"              desc="List cron jobs in the current cluster" />
        <EndpointRow method="GET"    path="/job-runner/v1/cron/get/{cron_id}"     desc="Get a single cron job config" />
        <EndpointRow method="PUT"    path="/job-runner/v1/cron/update/{cron_id}"  desc="Update a cron job config" />
        <EndpointRow method="PATCH"  path="/job-runner/v1/cron/toggle/{cron_id}?enabled=true" desc="Enable or disable (suspend) a cron job" />
        <EndpointRow method="DELETE" path="/job-runner/v1/cron/delete/{cron_id}"  desc="Delete a cron job" />
        <EndpointRow method="GET"    path="/job-runner/v1/cron/history/{cron_id}?limit=10" desc="List recent executions for a cron job" />
      </div>
      <P>Retrieve an execution's log with the standard job utility endpoints using the run's job id: <IC>POST /job-runner/v1/get-log?job_id=...</IC>, or <IC>GET /job-runner/v1/download-log?job_id=...</IC> for the complete log as a file.</P>

      <H3>Request Body Schema</H3>
      <Code lang="json">{`{
  "name": "nightly-backup",        // Display name for the cron job
  "schedule": "0 3 * * *",         // 5-field cron expression
  "image": "mybackuptool",         // MCR image name (no credentials)
  "timezone": "UTC",               // Optional — defaults to UTC
  "commandArgs": ["--full"],       // Optional
  "env": [                         // Optional environment variables
    { "name": "RETENTION_DAYS", "value": "30" }
  ],
  "resourceRequests": {            // Optional
    "cpu": 0.5,                    // vCPU cores
    "memory": 0.25                 // Memory in GB
  },
  "useGpu": false,                 // Optional — Premium only; applies to every run
  "enabled": true                  // Optional — defaults to true
}`}</Code>
      <Note><IC>useGpu</IC> applies to <strong>every</strong> execution of the schedule, so a GPU cron accrues GPU charges on each run. It is rejected with HTTP <IC>422</IC> at create/update time on non-Premium clusters rather than failing silently on the first tick.</Note>

      <H3>Example — Create a Cron Job</H3>
      <Code lang="bash">{`curl -X POST https://job.mermadicrunner.com/job-runner/v1/cron/create \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "every-5-min-task",
    "schedule": "*/5 * * * *",
    "image": "myprocessor",
    "commandArgs": ["python", "run.py"]
  }'`}</Code>

      <Note>Cron Jobs only accept MCR images. To use code from another registry, build and push it to MCR first via <strong>Container Registry → Build &amp; Push</strong>.</Note>
    </>
  ),

  api: (
    <>
      <H2>Dedicated API — API Guide</H2>
      <P>The dedicated API exposes separate endpoints per registry type, plus shared utility endpoints. All requests require an API key — see <strong>Getting Started → Quick Start</strong> for how to generate one.</P>

      <H3>Job Creation Endpoints</H3>
      <div className="docs-endpoint-list">
        <EndpointRow method="POST" path="/job-runner/v1/dockerhub/create-job" desc="DockerHub images (public or private)" />
        <EndpointRow method="POST" path="/job-runner/v1/ecr/create-job"       desc="AWS Elastic Container Registry (credentials required)" />
        <EndpointRow method="POST" path="/job-runner/v1/acr/create-job"       desc="Azure Container Registry (credentials required)" />
        <EndpointRow method="POST" path="/job-runner/v1/mcr/create-job"       desc="Mermadic Container Registry (no credentials needed)" />
                    </div>

      <P>Every <IC>create-job</IC> endpoint returns the new job id as a JSON string with HTTP <IC>201</IC> (for example <IC>"20260617143022100250"</IC>). Use that id with the utility endpoints below.</P>

      <H3>Utility Endpoints</H3>
      <div className="docs-endpoint-list">
        <EndpointRow method="POST" path="/job-runner/v1/get-log?job_id=..."        desc="Retrieve logs for a job — job_id is a query parameter" />
        <EndpointRow method="GET"  path="/job-runner/v1/download-log?job_id=..."   desc="Download the complete log as a text file — works for cron runs too" />
        <EndpointRow method="POST" path="/job-runner/v1/get-job-detail?job_id=..." desc="Get full job details — job_id is a query parameter" />
        <EndpointRow method="POST" path="/job-runner/v1/all-logs"                  desc="List jobs for your account with metadata — optional JSON filter body" />
        <EndpointRow method="POST" path="/job-runner/v1/delete-job?job_id=..."     desc="Stop a running job — only accepted for jobs that are currently running" />
      </div>
      <P>Logs are kept in full, but <IC>get-log</IC> and <IC>get-job-detail</IC> return only the tail of a very large one so responses stay small — they tell you the full size so you can fetch the whole thing from <IC>download-log</IC>.</P>
      <P><IC>get-log</IC>, <IC>get-job-detail</IC>, and <IC>delete-job</IC> take <IC>job_id</IC> as a <strong>query parameter</strong>. <IC>all-logs</IC> accepts an optional JSON body: <IC>recentMonths</IC> (default 3), <IC>showLimit</IC> (default 100), <IC>fromDate</IC>, and <IC>terminationStatus</IC> (<IC>all</IC> / <IC>terminated</IC> / <IC>running</IC>). It returns an array of job summary objects — see <IC>all-logs Response</IC> below.</P>

      <H3>all-logs Response</H3>
      <P>Each element in the <IC>logs</IC> array is a job summary object:</P>
      <Code lang="json">{`{
  "logs": [
    {
      "JobID":     "20260620143022100250",   // Unique job identifier
      "image":     "myprocessor",            // Image that was run
      "StartTime": "2026-06-20T14:30:22Z",  // ISO-8601 start timestamp
      "Status":    "Completed"              // Current status
    }
  ]
}`}</Code>
      <P>Possible <IC>Status</IC> values: <IC>running</IC>, <IC>Completed</IC>, <IC>Failed</IC>, <IC>OOMKilled</IC>, <IC>Error</IC>, <IC>Deleted</IC>, <IC>deleting</IC>, and other Kubernetes termination reasons.</P>

      <H3>Deleting a Job</H3>
      <P><IC>delete-job</IC> is accepted <strong>only for running jobs</strong>. Sending it for a job that has already finished returns HTTP <IC>400</IC> with <IC>"Only running jobs can be deleted."</IC> When accepted, the job is marked <IC>deleting</IC> and only the container process is killed — the pod remains until its TTL. The sidecar monitor finalizes billing and sets the status to <IC>Deleted</IC>.</P>

      <H3>Request Body Schema</H3>
      <Code lang="json">{`{
  "image": "string",               // Registry-qualified image name
  "commandArgs": ["string"],       // Command + arguments array
  "resourceRequests": {
    "cpu": 0.5,                    // vCPU cores (e.g. 0.5 = half a core)
    "memory": 1.0                  // Memory in GB
  },
  "credential": {                  // Omit for public / MCR images
    "dockerhubUsername": "...",    // DockerHub only
    "dockerhubPassword": "...",
    "awsAccessKeyId": "...",       // ECR only
    "awsSecretAccessKey": "...",
    "regionName": "us-east-1",
    "azureUsername": "...",        // ACR only
    "azurePassword": "..."
  },
  "env": [                         // Environment variables (optional)
    { "name": "KEY", "value": "VALUE" }
  ],
  "configmapName": "my-env",       // Optional (see note)
  "useGpu": false                  // Optional — Premium only (see note)
}`}</Code>
      <Note><IC>configmapName</IC> injects an existing ConfigMap's key/values as environment variables into the job. It works on every tier — on the shared cluster the ConfigMap is stored as an env file and materialized into a private per-job secret, on dedicated clusters it maps to a Kubernetes ConfigMap. The ConfigMap must already exist or the job is refused before it starts. Use the plain name you gave the ConfigMap (no user prefix).</Note>
      <Note><IC>useGpu</IC> runs the job on a GPU node with one NVIDIA T4 attached. It is available on <strong>Premium clusters only</strong> — sending it on Shared or Gold returns HTTP <IC>422</IC>. It defaults to <IC>false</IC>, so existing jobs are unaffected. GPU nodes scale to zero when idle, so the first GPU job after a quiet period takes a few minutes to start while a node is provisioned. GPU jobs are billed at a flat per-GPU-hour rate with a 15-minute minimum, which replaces the usual vCPU and memory charges — the GPU node's CPU and memory are included.</Note>

      <H3>Resource Limits by Tier</H3>
      <table className="docs-table">
        <thead><tr><th>Tier</th><th>Max vCPU / job</th><th>Max Memory / job</th><th>Node size</th></tr></thead>
        <tbody>
          <tr><td>Shared</td><td>1 vCPU</td><td>2 GB</td><td>Shared pool</td></tr>
          <tr><td>Gold</td><td>2 vCPU</td><td>3.5 GB</td><td>Medium nodes</td></tr>
          <tr><td>Premium</td><td>4 vCPU</td><td>14 GB</td><td>xlarge / 2xlarge nodes</td></tr>
          <tr><td>Premium + <IC>useGpu</IC></td><td>3 vCPU</td><td>12 GB</td><td>g4dn.xlarge (1× T4)</td></tr>
        </tbody>
      </table>
      <Note>GPU jobs have a lower CPU and memory ceiling than regular Premium jobs because they must fit on a single GPU node. Requesting more returns HTTP <IC>422</IC>.</Note>
    </>
  ),

  examples: (
    <>
      <H2>Common Use Cases</H2>

      <H3>ETL Job — DockerHub (public image)</H3>
      <P><strong>Endpoint:</strong> <IC>POST /job-runner/v1/dockerhub/create-job</IC></P>
      <Code lang="json">{`{
  "image": "apache/airflow:2.8.0",
  "commandArgs": ["python", "/scripts/etl.py", "--input", "s3://raw/", "--output", "s3://processed/"],
  "resourceRequests": { "cpu": 2, "memory": 4 },
  "env": [
    { "name": "AWS_ACCESS_KEY_ID",     "value": "AKIA..." },
    { "name": "AWS_SECRET_ACCESS_KEY", "value": "..." }
  ]
}`}</Code>

      <H3>Private Image — DockerHub</H3>
      <P><strong>Endpoint:</strong> <IC>POST /job-runner/v1/dockerhub/create-job</IC></P>
      <Code lang="json">{`{
  "image": "mycompany/data-processor:latest",
  "commandArgs": ["npm", "run", "process", "--", "--file=data.csv"],
  "resourceRequests": { "cpu": 1, "memory": 2 },
  "credential": {
    "dockerhubUsername": "mycompany",
    "dockerhubPassword": "dckr_pat_abc123"
  }
}`}</Code>

      <H3>Private Image — AWS ECR</H3>
      <P><strong>Endpoint:</strong> <IC>POST /job-runner/v1/ecr/create-job</IC></P>
      <Code lang="json">{`{
  "image": "123456789012.dkr.ecr.us-east-1.amazonaws.com/ml-inference:v2",
  "commandArgs": ["python", "inference.py", "--batch-size=32"],
  "resourceRequests": { "cpu": 2, "memory": 8 },
  "credential": {
    "awsAccessKeyId":     "asdgasdvsdv",
    "awsSecretAccessKey": "asdvasdvcxv",
    "regionName":         "us-east-1"
  }
}`}</Code>

      <H3>MCR Image — No Credentials Needed</H3>
      <P><strong>Endpoint:</strong> <IC>POST /job-runner/v1/mcr/create-job</IC> — use the image name exactly as it was stored (lowercased, spaces removed).</P>
      <Code lang="json">{`{
  "image": "myprocessor",
  "commandArgs": ["python", "run.py", "--mode=batch"],
  "resourceRequests": { "cpu": 1, "memory": 2 }
}`}</Code>

      <H3>Job With a ConfigMap</H3>
      <P><strong>Endpoint:</strong> <IC>POST /job-runner/v1/mcr/create-job</IC> — inject all key/values from a ConfigMap you created under ConfigMaps as environment variables. Works with any registry endpoint and on every tier.</P>
      <Code lang="json">{`{
  "image": "myprocessor",
  "commandArgs": ["python", "run.py"],
  "resourceRequests": { "cpu": 1, "memory": 2 },
  "configmapName": "my-app-env"
}`}</Code>
      <Note>The ConfigMap must already exist. If it does not, the job is rejected before it starts. <IC>configmapName</IC> is accepted on every tier — the shared cluster stores it as an env file, dedicated clusters as a Kubernetes ConfigMap.</Note>

      <H3>GPU Job — Premium Only</H3>
      <P><strong>Endpoint:</strong> <IC>POST /job-runner/v1/mcr/create-job</IC> — add <IC>useGpu</IC> to run on a GPU node with one NVIDIA T4. Your image must bundle its own CUDA runtime; the driver is provided by the node.</P>
      <Code lang="json">{`{
  "image": "trainingjob",
  "commandArgs": ["python", "train.py", "--epochs=10"],
  "resourceRequests": { "cpu": 3, "memory": 12 },
  "useGpu": true
}`}</Code>
      <Note>GPU nodes idle at zero, so expect a few minutes of start-up on the first GPU job after a quiet period. You are not charged while a job waits for a node — billing starts when your container starts. GPU jobs bill a flat per-GPU-hour rate with a 15-minute minimum, which covers the node's CPU and memory too; see <strong>Pricing</strong> for current rates.</Note>

      <H3>Azure ACR</H3>
      <P><strong>Endpoint:</strong> <IC>POST /job-runner/v1/acr/create-job</IC></P>
      <Code lang="json">{`{
  "image": "mycompany.azurecr.io/data-processor:v1.5",
  "commandArgs": ["process", "--input", "azure-blob://data/raw"],
  "resourceRequests": { "cpu": 2, "memory": 4 },
  "credential": {
    "azureUsername": "mycompany",
    "azurePassword": "azure_service_principal_password"
  }
}`}</Code>
    </>
  ),

  docker: (
    <>
      <H2>Sample Code</H2>

      <H3>Minimal Dockerfile</H3>
      <Code lang="bash">{`FROM python:3.11-slim
WORKDIR /app
COPY script.py .
CMD ["python", "script.py"]`}</Code>

      <H3>Argument Parsing (Python)</H3>
      <Code lang="python">{`import argparse

if __name__ == "__main__":
    parser = argparse.ArgumentParser()
    parser.add_argument("--input",  required=True)
    parser.add_argument("--output", required=True)
    args = parser.parse_args()
    print(f"Processing {args.input} → {args.output}")
`}</Code>

      <H3>Reading Environment Variables</H3>
      <Code lang="python">{`import os

db_url   = os.environ.get("DATABASE_URL", "sqlite:///local.db")
api_key  = os.environ.get("API_KEY")
s3_bucket = os.environ.get("S3_BUCKET", "my-default-bucket")

print(f"DB:  {db_url}")
print(f"S3:  {s3_bucket}")
`}</Code>

      <H3>Submitting a Job via Python</H3>
      <Code lang="python">{`import requests

API_KEY = "mmc_your_api_key_here"   # from My Account → Developer Access
BASE    = "https://job.mermadicrunner.com"

resp = requests.post(
    f"{BASE}/job-runner/v1/dockerhub/create-job",
    headers={"Authorization": f"Bearer {API_KEY}"},
    json={
        "image": "mycompany/processor:latest",
        "commandArgs": ["python", "run.py", "--input", "data.csv"],
        "resourceRequests": {"cpu": 1, "memory": 2},
        "credential": {
            "dockerhubUsername": "mycompany",
            "dockerhubPassword": "dckr_pat_xxx",
        },
        "env": [{"name": "OUTPUT_BUCKET", "value": "my-bucket"}],
    },
)
print(resp.json())  # {"job_id": "job-abc123", "status": "pending"}
`}</Code>
    </>
  ),

  mcr: (
    <>
      <H2>Mermadic Container Registry (MCR)</H2>
      <P>The Mermadic Container Registry is a private Docker registry included with your subscription. It is <strong>shared across your whole account</strong> — push an image once and run it on any of your clusters with no external credentials required.</P>

      <UiAccess>
        <ul className="docs-list">
          <li>In the navbar, click <strong>Images → <Link to="/mcr/registry">Registry</Link></strong>.</li>
          <li>View all your pushed images with their sizes and push timestamps.</li>
          <li>To build and push a new image, click <strong>Build & Push</strong>, enter an image name, upload your ZIP file, and click <strong>Build & Push</strong>.</li>
          <li>To delete an image, click the trash icon next to it in the image list.</li>
          <li>To view the build log, click <strong>Images → <Link to="/mcr/push-history">Build History</Link></strong> and select a build entry.</li>
        </ul>
      </UiAccess>

      <H3>Why Use MCR?</H3>
      <ul className="docs-list">
        <li><strong>Zero-credential jobs</strong> — MCR images are pre-authorized on your cluster. No <IC>credential</IC> block needed in your job request.</li>
        <li><strong>Faster cold starts</strong> — Images are cached on your cluster nodes, so pull time is near-zero.</li>
        <li><strong>Private by default</strong> — Images are only accessible to your account and the clusters you own.</li>
        <li><strong>Build in-cloud</strong> — Upload a ZIP containing a Dockerfile and Mermadic builds and pushes for you. No local Docker required.</li>
      </ul>

      <H3>Image Naming</H3>
      <P>When you push an image you provide a plain string as the image name. Spaces are automatically stripped and the name is lowercased. No username prefix or tag is required — the system manages those for you.</P>
      <Code lang="bash">{`# You supply just a plain string, for example:
"my processor"   →  stored as  "myprocessor"
"DataETL"        →  stored as  "dataetl"
"webhook-handler" →  stored as  "webhook-handler"`}</Code>

      <H3>Image Types: Job vs Deployment</H3>
      <P>Every image you build is one of two types, selected with the <IC>image_type</IC> field (and in the Registry toggle):</P>
      <ul className="docs-list">
        <li><strong>Job</strong> (<IC>image_type=short</IC>, default) — a lightweight image for one-off jobs and batch tasks run through the Dedicated API.</li>
        <li><strong>Deployment</strong> (<IC>image_type=long</IC>) — a persistent image for long-running APIs and services. Expose a <IC>/health</IC> endpoint so the load balancer can verify the service is up. Deployment images can only be <em>deployed</em> on a dedicated cluster.</li>
      </ul>
      <P>The two types are stored in separate repositories, so all listing and removal endpoints take the same <IC>image_type</IC> to indicate which set to act on.</P>

      <H3>MCR API Endpoints</H3>
      <div className="docs-endpoint-list">
        <EndpointRow method="GET"  path="/v1/cdci/mcr/images?image_type=short" desc="List images of a type (short = Job, long = Deployment)" />
        <EndpointRow method="POST" path="/v1/cdci/push-build"                  desc="Build an image from a ZIP and push to MCR (multipart)" />
        <EndpointRow method="POST" path="/v1/cdci/remove-mcr-image"            desc="Remove an image tag (multipart form: image_name, image_type)" />
        <EndpointRow method="GET"  path="/v1/cdci/builds"                      desc="List your builds with status and timings" />
        <EndpointRow method="GET"  path="/v1/cdci/build-log/{build_id}"        desc="Retrieve a build's log" />
        <EndpointRow method="POST" path="/v1/cdci/cancel-build/{build_id}"     desc="Cancel a running build" />
        <EndpointRow method="GET"  path="/v1/cdci/ci-log/{image_name}"         desc="Retrieve the latest build log for an image" />
      </div>

      <H3>Using MCR Images in Jobs</H3>
      <P>Use the image name you provided when pushing (after auto-formatting). No <IC>credential</IC> block is needed — your cluster is pre-authorized.</P>
      <Code lang="json">{`{
  "image": "dataprocessor",
  "commandArgs": ["python", "process.py", "--env=prod"],
  "resourceRequests": { "cpu": 1, "memory": 2 },
  "env": [
    { "name": "DATABASE_URL", "value": "postgresql://..." }
  ]
}`}</Code>

      <Note>MCR images do not require a <IC>credential</IC> field. Mermadic automatically injects the registry secret into your dedicated cluster at provisioning time.</Note>
      <Note>Jobs run <strong>Job</strong> (<IC>short</IC>) type images. Push your job images with the Job image type — Deployment (<IC>long</IC>) images live in a separate repository and are intended for the Deployment service.</Note>

      <H3>List Your Images</H3>
      <P>Pass <IC>image_type=short</IC> for Job images or <IC>image_type=long</IC> for Deployment images (defaults to <IC>short</IC>):</P>
      <Code lang="bash">{`curl -X GET "https://job.mermadicrunner.com/v1/cdci/mcr/images?image_type=short" \\
  -H "Authorization: Bearer YOUR_API_KEY"

# Response
[
  { "image_name": "dataprocessor", "created_on": "2026-03-01T10:22:00Z", "size_mb": 312.4 },
  { "image_name": "mlinference",   "created_on": "2026-02-28T08:14:00Z", "size_mb": 890.1 }
]`}</Code>

      <H3>Delete an Image</H3>
      <P>Removal is a <IC>POST</IC> with multipart form fields — specify the <IC>image_type</IC> matching the image:</P>
      <Code lang="bash">{`curl -X POST https://job.mermadicrunner.com/v1/cdci/remove-mcr-image \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -F "image_name=dataprocessor" \\
  -F "image_type=short"`}</Code>
    </>
  ),

  mcrpush: (
    <>
      <H2>Build & Push to MCR</H2>
      <P>Mermadic can build your Docker image in the cloud and push it directly to MCR. Upload a ZIP archive containing your source code and a <IC>Dockerfile</IC> at the root — that's it.</P>

      <UiAccess>
        <ul className="docs-list">
          <li>Go to <strong>Images → <Link to="/mcr/registry">Registry</Link></strong>.</li>
          <li>Click <strong>Build & Push</strong>, enter a name for the image, choose the <strong>Image Type</strong> (Job or Deployment), upload your ZIP file, and click <strong>Build & Push</strong>.</li>
          <li>No local Docker installation required — the build runs in the cloud.</li>
          <li>Track build progress under <strong>Images → <Link to="/mcr/push-history">Build History</Link></strong>.</li>
        </ul>
      </UiAccess>

      <H3>Choose an Image Type</H3>
      <P>Before building, select which kind of image you are pushing — this determines which repository it lands in and how it can be used:</P>
      <ul className="docs-list">
        <li><strong>Job</strong> (<IC>image_type=short</IC>, default) — a lightweight image to run as one-off jobs and batch tasks via the Dedicated API.</li>
        <li><strong>Deployment</strong> (<IC>image_type=long</IC>) — a persistent image for long-running APIs/services. Add a <IC>/health</IC> endpoint returning HTTP 200 so the load balancer can health-check it. Deployment images can be built on any tier, but can only be <em>deployed</em> on a dedicated cluster.</li>
      </ul>

      <H3>Requirements</H3>
      <ul className="docs-list">
        <li>A <IC>.zip</IC> archive with a <IC>Dockerfile</IC> at the root level</li>
        <li>An image name string — spaces and unsupported characters are removed automatically; no tag or username is required</li>
        <li>The following special characters are <strong>not allowed</strong> in the image name: <IC>! @ # $ % ^ &amp; * ( ) = + [ ] {'{ }'} | \ ; : ' " , &lt; &gt; ?</IC></li>
      </ul>

      <H3>API: Build & Push</H3>
      <div className="docs-endpoint-list">
        <EndpointRow method="POST" path="/v1/cdci/push-build"                  desc="Multipart form upload — builds and pushes to MCR" />
        <EndpointRow method="GET"  path="/v1/cdci/builds"                      desc="List your builds with status and timings" />
        <EndpointRow method="GET"  path="/v1/cdci/build-log/{build_id}"        desc="Retrieve a build's log" />
        <EndpointRow method="GET"  path="/v1/cdci/build-log/{build_id}/download" desc="Download the complete build log as a text file" />
        <EndpointRow method="POST" path="/v1/cdci/cancel-build/{build_id}"     desc="Cancel a running build" />
      </div>

      <H4>Form Fields</H4>
      <table className="docs-table">
        <thead><tr><th>Field</th><th>Type</th><th>Required</th><th>Description</th></tr></thead>
                        <tbody>
          <tr><td><IC>zip_file</IC></td><td>file</td><td>Yes</td><td>ZIP archive with Dockerfile at root</td></tr>
          <tr><td><IC>image_name</IC></td><td>string</td><td>Yes</td><td>Plain string (e.g. <IC>My Processor</IC>). Spaces and unsupported characters are removed automatically. No tag or username required.</td></tr>
          <tr><td><IC>image_type</IC></td><td>string</td><td>No</td><td><IC>short</IC> (Job, default) or <IC>long</IC> (Deployment)</td></tr>
          <tr><td><IC>app_name</IC></td><td>string</td><td>No</td><td>If set, a successful build triggers an auto-redeploy of this deployment (used by CDCI automation)</td></tr>
                        </tbody>
                    </table>

      <H3>Example — cURL</H3>
      <Code lang="bash">{`curl -X POST https://job.mermadicrunner.com/v1/cdci/push-build \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -F "zip_file=@./my-project.zip" \\
  -F "image_name=My Data Processor" \\
  -F "image_type=short"

# Response (HTTP 201 — build runs asynchronously)
{ "pushedImage": "MyDataProcessor", "buildId": "b-81a06bbf2f47" }`}</Code>

      <H3>Example — Python</H3>
      <Code lang="python">{`import requests

API_KEY = "mmc_your_api_key_here"   # from My Account → Developer Access
BASE    = "https://job.mermadicrunner.com"

with open("my-project.zip", "rb") as f:
    resp = requests.post(
        f"{BASE}/v1/cdci/push-build",
        headers={"Authorization": f"Bearer {API_KEY}"},
        files={"zip_file": ("my-project.zip", f, "application/zip")},
        data={"image_name": "My Data Processor", "image_type": "short"},
    )
print(resp.json())
# {"pushedImage": "MyDataProcessor", "buildId": "b-81a06bbf2f47"}
`}</Code>

      <H3>Checking Build Status</H3>
      <P>The <IC>buildId</IC> returned by <IC>push-build</IC> identifies the build. Poll its log while it runs — the log grows as the build progresses, so you do not have to wait for it to finish:</P>
      <Code lang="bash">{`# List your builds (newest first) with status and timings
curl -X GET https://job.mermadicrunner.com/v1/cdci/builds \\
  -H "Authorization: Bearer YOUR_API_KEY"

# Get the log for one build
curl -X GET https://job.mermadicrunner.com/v1/cdci/build-log/b-81a06bbf2f47 \\
  -H "Authorization: Bearer YOUR_API_KEY"

# Or fetch the latest build log by image name
curl -X GET "https://job.mermadicrunner.com/v1/cdci/ci-log/mydataprocessor" \\
  -H "Authorization: Bearer YOUR_API_KEY"

# Stop a build you no longer need
curl -X POST https://job.mermadicrunner.com/v1/cdci/cancel-build/b-81a06bbf2f47 \\
  -H "Authorization: Bearer YOUR_API_KEY"`}</Code>
      <P>A build reports one of <IC>queued</IC>, <IC>running</IC>, <IC>success</IC>, <IC>failed</IC>, or <IC>cancelled</IC>. <IC>build-log</IC> returns the tail of the log and sets <IC>truncated</IC> when there is more; append <IC>/download</IC> to the same path for the complete file.</P>

      <H3>ZIP Structure Requirements</H3>
      <Code lang="bash">{`my-project.zip
├── Dockerfile          ← required at root
├── requirements.txt
├── script.py
└── data/
    └── config.yaml`}</Code>

      <Note>The Dockerfile must be at the <strong>root</strong> of the ZIP (not inside a subdirectory). Build context is the entire ZIP contents.</Note>

      <H3>Using the Dashboard</H3>
      <P>You can also build and push via the <strong>Registry</strong> page in the dashboard — no CLI required. Navigate to <strong>Registry → Build & Push</strong>, enter your image name, upload the ZIP, and click Build & Push.</P>
    </>
  ),

  'cdci-overview': (
    <>
      <H2>Automations Overview</H2>
      <P>Mermadic Automation connects a GitHub repository branch to one of your deployed apps. Every time you push code to that branch, the pipeline automatically builds a new container image, pushes it to your registry, and redeploys your app — with zero manual steps.</P>

      <UiAccess>
        <ul className="docs-list">
          <li>In the navbar, click <strong>Images → <Link to="/cdci/automation">Automations</Link></strong>.</li>
          <li>Click <strong>+ New</strong> in the sidebar to create an automation pipeline.</li>
          <li>The wizard walks through: App selection, GitHub connection, ConfigMap, Deployment Config, and Activate.</li>
          <li>Existing automations are listed in the sidebar — click one to view or edit its configuration.</li>
          <li>Toggle <strong>Auto Deploy</strong> on the Activate step to control whether each build triggers an automatic redeploy.</li>
        </ul>
      </UiAccess>

      <H3>What the Pipeline Does</H3>
      <ul className="docs-list">
        <li><strong>1. Code push</strong> — A push to your configured branch triggers the pipeline via the GitHub App webhook.</li>
        <li><strong>2. Build</strong> — Mermadic downloads your repository as a ZIP, builds the Docker image using your <IC>Dockerfile</IC>, and pushes it to MCR.</li>
        <li><strong>3. Deploy</strong> — If <strong>Auto Deploy</strong> is enabled, the deployment is automatically updated with the new image and you receive an email notification once it has successfully restarted. If disabled, you receive an email notification that the build is ready and can trigger the redeploy manually.</li>
        <li><strong>4. Failure notification</strong> — If the build or deploy fails at any step, you are notified by email regardless of the Auto Deploy setting.</li>
      </ul>

      <H3>Auto Deploy Toggle</H3>
      <P>Each automation has an <strong>Auto Deploy</strong> toggle (off by default):</P>
      <ul className="docs-list">
        <li><strong>On</strong> — Every successful build immediately redeploys your app with a rolling restart. You receive an email notification once the deployment has successfully restarted.</li>
        <li><strong>Off</strong> — A successful build sends you an email notification. You redeploy manually from Deployments when ready.</li>
      </ul>

      <H3>Retry Build</H3>
      <P>If the initial build fails (e.g. a Dockerfile issue), the <strong>Activate Automation</strong> button remains visible on the review step so you can re-trigger the build once you have fixed your repository — without recreating the automation config.</P>

      <H3>Requirements</H3>
      <ul className="docs-list">
        <li>A GitHub App installation granting Mermadic access to the target repository.</li>
        <li>A <IC>Dockerfile</IC> at the <strong>root</strong> of your repository (see layout below).</li>
        <li>An MCR image name and a Deployment Config created in Deployments.</li>
        <li>A dedicated cluster account.</li>
      </ul>

      <H4>Repository Layout</H4>
      <P>The <IC>Dockerfile</IC> must sit at the top level of the repository — not inside a subfolder. Mermadic builds from the repository root, so everything your build needs should be reachable from there.</P>
      <Code lang="bash">{`my-api/                  ← repository root
├── Dockerfile           ← required, at the root
├── requirements.txt
├── main.py
├── app/
│   ├── __init__.py
│   └── routes.py
└── README.md`}</Code>
      <Note>If your <IC>Dockerfile</IC> lives in a subdirectory (e.g. <IC>docker/Dockerfile</IC>), the build will fail to find it. Move it to the repository root before connecting the automation.</Note>
    </>
  ),

  'cdci-github': (
    <>
      <H2>GitHub Integration</H2>
      <P>Mermadic uses a GitHub App to receive push event webhooks from your repositories. The App needs to be installed on the repositories you want to automate.</P>

      <UiAccess>
        <ul className="docs-list">
          <li>Go to <strong>Images → <Link to="/cdci/automation">Automations</Link></strong> → <strong>+ New</strong>.</li>
          <li>On the <strong>GitHub</strong> step, click <strong>Connect GitHub App</strong> to grant Mermadic access to your repositories.</li>
          <li>Select the repository and enter the branch name. The branch is validated before you can proceed.</li>
          <li>To add more repositories later, click <strong>Edit GitHub Access</strong> on any existing automation.</li>
        </ul>
      </UiAccess>

      <H3>Connecting GitHub</H3>
      <ul className="docs-list">
        <li>In <strong>Images → <Link to="/cdci/automation">Automations</Link> → + New</strong>, proceed to the <strong>GitHub</strong> step.</li>
        <li>If the GitHub App is not yet installed, click <strong>Connect GitHub App</strong>. This opens GitHub where you grant access to one or more repositories.</li>
        <li>Once connected, a green <strong>GitHub connected</strong> badge appears.</li>
        <li>Select the <strong>repository</strong> from the dropdown and type the <strong>branch</strong> name. The branch is validated against the GitHub API before you can proceed.</li>
      </ul>

      <H3>Editing Repository Access</H3>
      <P>Click <strong>Edit GitHub Access</strong> (the dark GitHub button) at any time — in both new and existing automations — to open GitHub and add or remove repositories from the App installation.</P>

      <H3>Branch Input</H3>
      <P>Rather than a dropdown, Mermadic asks you to type the branch name directly. This avoids loading large branch lists and allows you to type branches that may not yet exist locally. The system validates the branch exists on the remote before saving.</P>

      <H3>Webhook Routing</H3>
      <P>Because a GitHub App supports only one webhook URL, all push events are received by the Mermadic account service and routed to the correct cluster's dedicated API based on the automation configuration. You do not need to configure per-cluster webhook URLs.</P>
    </>
  ),

  'cdci-pipeline': (
    <>
      <H2>Pipeline Flow</H2>
      <P>Once an automation is activated, the full flow from push to live deployment looks like this:</P>

      <UiAccess>
        <ul className="docs-list">
          <li>View all automation pipelines and their status in <strong>Images → <Link to="/cdci/automation">Automations</Link></strong>.</li>
          <li>Click any automation to see its full configuration and activate/edit it.</li>
          <li>Monitor build output in <strong>Images → <Link to="/mcr/push-history">Build History</Link></strong>.</li>
        </ul>
      </UiAccess>
      <Code lang="bash">{`1. git push origin <branch>
   ↓
2. GitHub sends push event to Mermadic webhook
   ↓
3. Account service routes event to your cluster's dedicated API
   ↓
4. The dedicated API downloads the repository ZIP from GitHub
   ↓
5. Docker build runs using your Dockerfile
   ↓
6. Image pushed to MCR (tagged with your configured image name)
   ↓
7a. Auto Deploy ON  → deployment updated + rolling restart
                     → email notification sent on successful restart
7b. Auto Deploy OFF → email notification sent (redeploy manually)
   ↓
8. On any failure → email notification sent`}</Code>

      <H3>Automation Configuration Fields</H3>
      <table className="docs-table">
        <thead><tr><th>Field</th><th>Description</th></tr></thead>
        <tbody>
          <tr><td>App Name</td><td>Identifies which deployment config to update on each build</td></tr>
          <tr><td>Repository</td><td>GitHub repo the automation watches (e.g. <IC>myorg/my-api</IC>)</td></tr>
          <tr><td>Branch</td><td>Branch name that triggers the pipeline on push</td></tr>
          <tr><td>Image Name</td><td>MCR image tag that is built and pushed</td></tr>
          <tr><td>ConfigMap Name</td><td>Optional ConfigMap injected into the deployment</td></tr>
          <tr><td>Deployment Name</td><td>The deployment resource updated after a successful build</td></tr>
          <tr><td>Auto Deploy</td><td>Whether to redeploy automatically on a successful build</td></tr>
        </tbody>
      </table>

      <H3>Viewing & Editing an Existing Automation</H3>
      <P>Click any automation in the sidebar to open its config. All five steps (App, GitHub, ConfigMap, Deploy Config, Activate) are displayed as clickable tab icons at the top. You can navigate between steps, edit fields, and click <strong>Save Config</strong> to save without triggering a build, or <strong>Activate Automation</strong> to trigger one immediately.</P>

      <H3>Deleting an Automation</H3>
      <P>Click the <strong>✕</strong> button next to an automation in the sidebar. This removes the automation config — future pushes to the linked branch will no longer trigger builds. The deployment itself is not affected.</P>
    </>
  ),

  'deploy-overview': (
    <>
      <H2>Deployments</H2>
      <P>Deployments let you run long-lived services — APIs, web apps, background workers — on your dedicated cluster. Unlike one-off jobs, deployments stay running and are reachable via a public HTTPS URL. You can use an automatically generated hostname or attach your own custom domain.</P>

      <UiAccess>
        <ul className="docs-list">
          <li>In the navbar, click <strong>Apps → <Link to="/deployment/apps">Deployments</Link></strong> to view and manage all your deployed apps.</li>
          <li>Click <strong>+ New</strong> to create a deployment — pick an MCR image, set replicas, and optionally attach a ConfigMap and custom domain.</li>
          <li>Click <strong><Link to="/deployment/config">ConfigMaps</Link></strong> to create and manage ConfigMaps (environment variable sets).</li>
          <li>Click <strong>Apps → <Link to="/deployment/domains">Domains</Link></strong> to register and verify your own domains.</li>
          <li>Each deployment shows live pod health, replica counts, and the last deployed timestamp in the sidebar.</li>
        </ul>
      </UiAccess>

      <H3>How It Works</H3>
      <ul className="docs-list">
        <li><strong>Create a Deployment Config</strong> — Give your app a name, select a container image from MCR, optionally choose a ConfigMap for environment variables, and set the number of replicas.</li>
        <li><strong>Deploy</strong> — Mermadic creates the service, configures the load balancer, and provisions an HTTPS URL automatically.</li>
        <li><strong>Manage</strong> — Scale replicas, update the image or config, redeploy with zero downtime, stop, or delete at any time.</li>
        <li><strong>Custom Domain</strong> — Optionally assign a verified custom domain and a URL prefix to any deployment (e.g. <IC>api.yourdomain.com</IC>).</li>
      </ul>

      <H3>Two Image Types — Pick the Right One</H3>
      <P>When you build and push to MCR you choose an <strong>image type</strong>. This choice matters for deployments:</P>
      <ul className="docs-list">
        <li><strong>Job</strong> (<IC>short</IC>) — lightweight images for one-off jobs and batch tasks run through the Dedicated API. <em>These cannot be deployed.</em></li>
        <li><strong>Deployment</strong> (<IC>long</IC>) — persistent images for long-running APIs and services. <strong>Choose this type</strong> for anything you want to deploy. Deployment images only appear in the Deployments image picker.</li>
      </ul>
      <Note>If your image is missing from the deployment image dropdown, it was most likely pushed as a <strong>Job</strong> image. Re-push it from Registry with the <strong>Deployment</strong> image type selected.</Note>

      <H3>Requirements</H3>
      <ul className="docs-list">
        <li><strong>Dedicated cluster</strong> — Deployment features are only available for accounts with a dedicated cluster.</li>
        <li><strong>Deployment (long) MCR image</strong> — Build and push your image from Registry with the <strong>Deployment</strong> image type selected.</li>
        <li><strong>Health endpoint</strong> — Your application must expose a <IC>/health</IC> endpoint that returns HTTP 200. The load balancer uses this to verify your service is running.</li>
        <li><strong>ConfigMap</strong> — Optional. Create one under ConfigMaps if your app needs environment variables injected at runtime.</li>
      </ul>

      <H3>Default App URL</H3>
      <P>Once deployed, your application is accessible at an automatically assigned hostname:</P>
      <Code lang="bash">{`https://<cluster-name>-<app-name>.mermadicrunner.com`}</Code>
      <P>DNS propagates within 5 minutes of the first deployment. You can also attach a custom domain — see <strong>Domains</strong> and <strong>Custom Hosts</strong>.</P>
    </>
  ),

  'deploy-guide': (
    <>
      <H2>Step-by-Step Deployment Guide</H2>

      <UiAccess>
        <ul className="docs-list">
          <li>Start at <strong>Images → <Link to="/mcr/registry">Registry</Link></strong> to build and push your image.</li>
          <li>Then go to <strong><Link to="/deployment/config">ConfigMaps</Link></strong> if your app needs environment variables.</li>
          <li>Finally, open <strong>Apps → <Link to="/deployment/apps">Deployments</Link></strong> → <strong>+ New</strong> to create and launch your app.</li>
        </ul>
      </UiAccess>

      <H3>1. Push a Deployment Image to MCR</H3>
      <P>Navigate to <strong>Registry</strong>. Upload a ZIP archive containing your <IC>Dockerfile</IC> and source code, give the image a name, and set the <strong>Image Type</strong> to <strong>Deployment</strong> — only Deployment-type images can be deployed. Click <strong>Build & Push</strong>. Make sure your app exposes a <IC>/health</IC> endpoint that returns HTTP 200.</P>

      <H3>2. (Optional) Create a ConfigMap</H3>
      <P>If your app needs environment variables, create a ConfigMap first:</P>
      <ul className="docs-list">
        <li><strong>Dashboard:</strong> Go to <strong>ConfigMaps</strong> → <strong>+ New</strong>. Enter key/value pairs directly or upload a <IC>.env</IC> file.</li>
        <li><strong>API (key-value):</strong> <IC>POST /v1/deployment/create-configmap?name=my-env</IC></li>
        <li><strong>API (.env file):</strong> <IC>POST /v1/deployment/create-configmap-from-file?name=my-env</IC></li>
      </ul>
      <Note>ConfigMap keys must be valid environment variable names — letters, digits and underscores, starting with a letter or underscore (e.g. <IC>DATABASE_URL</IC>, <IC>apiKey</IC>). Case is preserved exactly as you enter it. A ConfigMap is not required if your app has no environment variables.</Note>

      <H3>3. Create a Deployment Config</H3>
      <P>In <strong>Deployments</strong>, click <strong>+ New</strong> and fill in:</P>
      <ul className="docs-list">
        <li><strong>App Name</strong> — Lowercase letters, numbers, hyphens. Determines your default URL.</li>
        <li><strong>MCR Image</strong> — Select the image pushed in step 1.</li>
        <li><strong>ConfigMap</strong> — Optional. Select if your app needs env vars.</li>
        <li><strong>Replicas</strong> — Number of running instances (default: 1).</li>
        <li><strong>Container Port</strong> — The port your app listens on inside the container.</li>
        <li><strong>Custom Domain Host</strong> — Optional. Enter a URL prefix and select a verified domain to use a custom hostname (e.g. <IC>api.yourdomain.com</IC>).</li>
      </ul>

      <H3>4. Deploy</H3>
      <P>Click <strong>Deploy App</strong>. Mermadic will create the deployment, its internal service, an ingress rule, and a DNS record for your URL. The <strong>Last Deployed</strong> timestamp is recorded and shown in the sidebar.</P>
      <Note>After the first deployment, DNS may take up to 5 minutes to propagate.</Note>

      <H3>5. Monitor</H3>
      <P>Click your deployment in the sidebar to see live status: replica counts, pod health, container states, restart counts, and the last deployed time. A green <strong>Healthy</strong> badge means all replicas are ready.</P>

      <H3>6. Update or Redeploy</H3>
      <P>Click <strong>Edit</strong> on the view panel, make changes, and click <strong>Deploy App</strong>. The update is applied with a zero-downtime rolling restart. If you use Automation, every push to your configured branch triggers a redeploy automatically.</P>

      <H3>7. Stop or Delete</H3>
      <ul className="docs-list">
        <li><strong>Stop</strong> — Removes running pods and the service but keeps the config. You can redeploy later.</li>
        <li><strong>Delete</strong> — Stops the deployment (if running) and removes the config entirely.</li>
      </ul>
    </>
  ),

  'deploy-configmap': (
    <>
      <H2>ConfigMaps</H2>
      <P>ConfigMaps store environment variables that are injected into your deployment container at runtime — no image rebuild needed. Using a ConfigMap is <strong>optional</strong>; skip it if your app has no env var requirements.</P>

      <Note>ConfigMap values can be read back at any time. For API keys, passwords, and tokens use a <strong>Secret</strong> instead (see the Secrets section) — same injection, but values are encrypted and never returned.</Note>

      <UiAccess>
        <ul className="docs-list">
          <li>Go to <strong><Link to="/deployment/config">ConfigMaps</Link></strong>.</li>
          <li>Click <strong>+ New</strong>, enter key/value pairs directly or upload a <IC>.env</IC> file.</li>
          <li>Keys must be valid environment variable names (e.g. <IC>DATABASE_URL</IC>); case is preserved.</li>
          <li>Once created, the ConfigMap appears in the sidebar and can be selected when configuring a deployment.</li>
        </ul>
      </UiAccess>

      <H3>Creating a ConfigMap</H3>

      <H4>Option A: Dashboard (ConfigMaps)</H4>
      <P>Navigate to <strong>ConfigMaps → + New</strong>. Enter key/value pairs one at a time, or switch to the <strong>.env File</strong> tab to upload a file in bulk. Keys are stored with the exact case you enter.</P>

      <H4>Option B: Upload a .env File via API</H4>
      <Code lang="bash">{`# .env file contents:
DATABASE_URL=postgresql://user:pass@host:5432/mydb
API_KEY=sk-abc123
LOG_LEVEL=INFO`}</Code>
      <Code lang="bash">{`curl -X POST "https://job.mermadicrunner.com/v1/deployment/create-configmap-from-file?name=my-app-env" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -F "env_file=@.env"`}</Code>

      <H4>Option C: JSON Key-Value Pairs via API</H4>
      <Code lang="bash">{`curl -X POST "https://job.mermadicrunner.com/v1/deployment/create-configmap?name=my-app-env" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "DATABASE_HOST": "db.internal",
    "FEATURE_CHECKOUT_V2": "true",
    "LOG_LEVEL": "INFO"
  }'`}</Code>

      <H3>Viewing & Managing ConfigMaps</H3>
      <div className="docs-endpoint-list">
        <EndpointRow method="GET"    path="/v1/deployment/list-configmap"             desc="List all ConfigMap names" />
        <EndpointRow method="GET"    path="/v1/deployment/view-configmap?name=..."    desc="View ConfigMap key-value data" />
        <EndpointRow method="DELETE" path="/v1/deployment/delete-configmap?name=..."  desc="Delete a ConfigMap (blocked if used by a deployment)" />
      </div>

      <Note>A ConfigMap cannot be deleted while it is referenced by an active deployment. Remove or update the deployment first, then delete the ConfigMap.</Note>
    </>
  ),

  'deploy-secrets': (
    <>
      <H2>Secrets</H2>
      <P>A Secret holds the same kind of env-style key/value data as a ConfigMap, for values you should not be able to read back — API keys, database passwords, signing keys, tokens. Secrets are write-only: the dashboard and the API return key names, never values. They work on every tier and can be attached to jobs, cron jobs and deployments.</P>

      <UiAccess>
        <ul className="docs-list">
          <li>Go to <strong>Config → <Link to="/deployment/secrets">Secrets</Link></strong>.</li>
          <li>Click <strong>+ New</strong>, enter key/value pairs directly or upload a <IC>.env</IC> file.</li>
          <li>Selecting a Secret shows its key names with masked values. <strong>Replace Values</strong> overwrites the whole Secret — every key has to be re-entered, because the current values cannot be read back.</li>
          <li>Attach a Secret when creating a job or cron job, or on a deployment&rsquo;s configuration.</li>
        </ul>
      </UiAccess>

      <H3>Creating a Secret</H3>

      <H4>Option A: Upload a .env File via API</H4>
      <Code lang="bash">{`curl -X POST "https://job.mermadicrunner.com/v1/secret/create-from-file?name=my-app-credentials" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -F "env_file=@.env"`}</Code>

      <H4>Option B: JSON Key-Value Pairs via API</H4>
      <Code lang="bash">{`curl -X POST "https://job.mermadicrunner.com/v1/secret/create?name=my-app-credentials" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "DATABASE_PASSWORD": "s3cr3t",
    "STRIPE_API_KEY": "sk_live_abc123"
  }'`}</Code>

      <Note>Posting a Secret that already exists replaces it in full — there is no partial update, since we cannot merge with values we never return.</Note>

      <H3>Using a Secret</H3>
      <P>Pass <IC>secretName</IC> on any create-job or cron-job request, or set the Secret field on a deployment config. Its keys arrive in the container as environment variables, exactly like a ConfigMap&rsquo;s.</P>
      <Code lang="bash">{`{
  "image": "my-image:latest",
  "secretName": "my-app-credentials"
}`}</Code>
      <Note>A job or cron job is refused before it starts if the Secret does not exist. If a Secret and a ConfigMap define the same key, the Secret&rsquo;s value wins.</Note>

      <H3>Viewing &amp; Managing Secrets</H3>
      <div className="docs-endpoint-list">
        <EndpointRow method="GET"    path="/v1/secret/list"             desc="List all Secret names" />
        <EndpointRow method="GET"    path="/v1/secret/keys?name=..."    desc="List the key names a Secret holds (never values)" />
        <EndpointRow method="DELETE" path="/v1/secret/delete?name=..."  desc="Delete a Secret (blocked if used by a deployment)" />
      </div>

      <H3>How values are protected</H3>
      <ul className="docs-list">
        <li>Encrypted with authenticated encryption (AES + HMAC) before they are written, under a prefix private to your account, on storage that is itself encrypted at rest. The stored file is unreadable without Mermadic&rsquo;s key, and a tampered file fails to decrypt rather than decoding to something wrong.</li>
        <li>Decrypted only when a workload starts, then materialized into a Kubernetes Secret that lives as long as the workload — values never appear in a pod spec, and a job&rsquo;s Secret is removed with the job.</li>
        <li>No endpoint returns a value — not to you, and not to Mermadic support. A lost value has to be replaced.</li>
        <li>Changes apply on the next job or cron run, and on the next <strong>Redeploy</strong> for a running deployment.</li>
      </ul>
    </>
  ),

  'deploy-domains': (
    <>
      <H2>Domains</H2>
      <P>Register your own domain with Mermadic to use it as the public hostname for any deployment. Mermadic requests a wildcard TLS certificate covering both <IC>*.yourdomain.com</IC> and <IC>yourdomain.com</IC>, verified via a CNAME DNS record you add to your DNS provider.</P>

      <UiAccess>
        <ul className="docs-list">
          <li>Go to <strong>Apps → <Link to="/deployment/domains">Domains</Link></strong>.</li>
          <li>Click <strong>+ Add Domain</strong>, enter your bare domain, and follow the CNAME verification steps.</li>
          <li>Click <strong>↻ Check Status</strong> to poll for verification. Once verified, select the domain in any deployment config.</li>
        </ul>
      </UiAccess>

      <H3>How to Add a Domain</H3>
      <ul className="docs-list">
        <li>Go to <strong>Apps → <Link to="/deployment/domains">Domains</Link></strong> and click <strong>+ Add Domain</strong>.</li>
        <li>Enter your bare domain (e.g. <IC>example.com</IC> — no <IC>www</IC>).</li>
        <li>Click <strong>Request Certificate</strong>. Mermadic provisions a certificate and displays the CNAME record required for verification.</li>
        <li>Add the CNAME record in your DNS provider.</li>
        <li>Click <strong>↻ Check Status</strong> in the sidebar. Verification typically completes within 5–30 minutes of DNS propagation.</li>
        <li>Once verified, the domain is available to select as a custom host in Deployments.</li>
      </ul>

      <H3>Limits</H3>
      <ul className="docs-list">
        <li>Up to <strong>10 domains</strong> per account.</li>
        <li>The certificate covers both <IC>*.yourdomain.com</IC> (wildcard) and the apex <IC>yourdomain.com</IC>, so any prefix you set in Deployments is automatically included.</li>
      </ul>

      <H3>Deleting a Domain</H3>
      <P>Click the domain in the sidebar and select <strong>Delete</strong>. This removes the domain record and the associated certificate. Before deleting, ensure no running deployment is actively using the certificate — otherwise the deployment's HTTPS will break until it is redeployed with a different host.</P>

      <H3>API Endpoints</H3>
      <div className="docs-endpoint-list">
        <EndpointRow method="POST"   path="/v1/domain/add"     desc="Register a new domain and request a certificate" />
        <EndpointRow method="GET"    path="/v1/domain/list"    desc="List all domains and their verification status" />
        <EndpointRow method="GET"    path="/v1/domain/verified" desc="List only verified domains (used by deployment dropdowns)" />
        <EndpointRow method="GET"    path="/v1/domain/check"   desc="Poll unverified domains and update their status" />
        <EndpointRow method="DELETE" path="/v1/domain/delete"  desc="Remove a domain and delete its certificate" />
      </div>
    </>
  ),

  'deploy-host': (
    <>
      <H2>Custom Hosts</H2>
      <P>Assign a custom hostname to any deployment by combining a URL prefix with a verified domain. For example, a prefix of <IC>api</IC> and domain <IC>example.com</IC> produces the host <IC>api.example.com</IC>.</P>

      <UiAccess>
        <ul className="docs-list">
          <li>First verify your domain at <strong>Apps → <Link to="/deployment/domains">Domains</Link></strong>.</li>
          <li>Then open your deployment in <strong>Apps → <Link to="/deployment/apps">Deployments</Link></strong>.</li>
          <li>Under <strong>Custom Domain Host</strong>, enter a URL prefix and select your verified domain, then save.</li>
        </ul>
      </UiAccess>

      <H3>Setting Up a Custom Host</H3>
      <ul className="docs-list">
        <li>First, add and verify your domain in <strong>Apps → <Link to="/deployment/domains">Domains</Link></strong>.</li>
        <li>In <strong>Apps → <Link to="/deployment/apps">Deployments</Link></strong>, open a deployment config (new or existing).</li>
        <li>Under <strong>Custom Domain Host</strong>, enter a <strong>URL prefix</strong> (lowercase, no dots — e.g. <IC>api</IC>, <IC>app</IC>, <IC>www</IC>) and select your verified domain from the dropdown.</li>
        <li>Click <strong>Save Config</strong> or <strong>Deploy App</strong>. The constructed hostname is stored in the deployment config and added as an ingress rule.</li>
      </ul>

      <H3>Default vs Custom Hostname</H3>
      <table className="docs-table">
        <thead><tr><th>Scenario</th><th>Resulting Hostname</th></tr></thead>
        <tbody>
          <tr><td>No custom domain set</td><td><IC>{'<cluster>-<app>.mermadicrunner.com'}</IC></td></tr>
          <tr><td>Prefix <IC>api</IC> + domain <IC>example.com</IC></td><td><IC>api.example.com</IC></td></tr>
          <tr><td>Prefix <IC>www</IC> + domain <IC>example.com</IC></td><td><IC>www.example.com</IC></td></tr>
        </tbody>
      </table>

      <Note>Custom hosts require the domain to be verified first. The wildcard certificate obtained during domain verification already covers any prefix you choose, so no additional certificate is needed per deployment.</Note>
    </>
  ),

  architecture: (
    <>
      <H2>Architecture</H2>
      <P>Each customer gets a fully isolated, dedicated infrastructure stack — from the network layer up. Nothing is shared between accounts.</P>

      <H3>Dedicated Private VPC Per Account</H3>
      <P>Isolation at Mermadic starts at the <strong>network layer</strong>. Every dedicated account is provisioned its own private AWS Virtual Private Cloud (VPC). Your clusters live in subnets that are completely unreachable by any other customer — even at the network level.</P>
      <div className="docs-diagram">
        <img src={architectureVpcImg} alt="Each Mermadic account runs in its own private AWS VPC, fully isolated from other customers" />
      </div>

      <Note>All your dedicated clusters — Gold and Premium — share the same private VPC, keeping internal traffic fast and low-latency. When you delete your last cluster, the VPC is automatically torn down.</Note>

      <H3>Auto-Scaling Flow</H3>
      <div className="docs-diagram">
        <img src={architectureAutoscaleImg} alt="Auto-scaling flow: jobs enter your cluster, scale up if busy, then scale down when idle" />
      </div>

      <H3>What Mermadic Manages</H3>
      <ul className="docs-list">
        {[
          'Private VPC provisioning and lifecycle (created on first cluster, torn down on last deletion)',
          'Cluster provisioning, upgrades, and security patches',
          'Auto-scaler configuration',
          'Load balancer and SSL/TLS certificate management',
          'Monitoring and logging infrastructure',
          'Private container registry provisioning and maintenance',
          'Backup and self-managed disaster recovery',
          'Webhook routing for CDCI automation',
          'DNS record management for default and custom hostnames',
        ].map(i => <li key={i}>✅ {i}</li>)}
      </ul>

      <H3>What You Control</H3>
      <ul className="docs-list">
        {[
          'Job submission — what to run and when',
          'Docker images (your containers, your code)',
          'Resource allocation per job',
          'Deployment configs, replicas, and environment variables',
          'Automation pipelines and GitHub branch connections',
          'Custom domains and hostname routing',
          'API access and account credentials',
        ].map(i => <li key={i}>✅ {i}</li>)}
      </ul>
    </>
  ),

  'vpc-isolation': (
    <>
      <H2>Network Isolation (VPC)</H2>
      <P>
        Most managed platforms isolate your <em>pods</em> from other tenants' pods using Kubernetes namespaces or node selectors.
        Mermadic goes one layer deeper: every dedicated account gets its own <strong>private AWS Virtual Private Cloud (VPC)</strong>.
        Your clusters run inside subnets that are completely unreachable by any other customer — not just at the application layer, but at the network layer.
      </P>

      <H3>What a Dedicated VPC Means for You</H3>
      <ul className="docs-list">
        <li><strong>No cross-tenant network paths.</strong> Packets from other customers' workloads cannot reach your subnets. There is no shared network fabric between accounts.</li>
        <li><strong>Private CIDR range.</strong> Your VPC is assigned its own non-overlapping private IP range. All internal cluster traffic stays within that range.</li>
        <li><strong>All your clusters share one VPC.</strong> Your Gold and Premium clusters are co-located in the same VPC, so internal traffic between them is fast and does not leave your private network.</li>
        <li><strong>Automatic lifecycle management.</strong> When you provision your first dedicated cluster, Mermadic creates your VPC. When you delete your last cluster, the VPC is torn down automatically — no orphaned resources, no lingering cost.</li>
      </ul>

      <H3>How It Compares</H3>
      <table className="docs-table">
        <thead>
          <tr><th>Isolation Layer</th><th>Typical Managed Platform</th><th>Mermadic</th></tr>
        </thead>
        <tbody>
          <tr><td>Network (VPC)</td><td>Shared with all tenants</td><td>Dedicated per account</td></tr>
          <tr><td>Subnets</td><td>Shared</td><td>Private, per-account CIDR</td></tr>
          <tr><td>Kubernetes cluster</td><td>Shared or dedicated</td><td>Dedicated per cluster</td></tr>
          <tr><td>Compute nodes</td><td>Shared or dedicated</td><td>Dedicated</td></tr>
          <tr><td>API endpoint</td><td>Shared</td><td>Dedicated per cluster</td></tr>
        </tbody>
      </table>

      <H3>VPC Limits &amp; Scaling</H3>
      <P>Each VPC supports up to 16 dedicated clusters. If you scale beyond that, Mermadic provisions a second VPC for your account and continues routing your additional clusters there — all automatically and transparently.</P>

      <Note>Dedicated VPC isolation is available on the <strong>Gold</strong> and <strong>Premium</strong> tiers. The Shared tier runs on Mermadic's shared infrastructure.</Note>
    </>
  ),

  benefits: (
    <>
      <H2>Why Mermadic</H2>

      <H3>vs DIY Kubernetes</H3>
      <table className="docs-table">
        <thead><tr><th>Feature</th><th>DIY K8s</th><th>Mermadic</th></tr></thead>
        <tbody>
          <tr><td>Setup Time</td><td>2–6 months</td><td>~1 hour</td></tr>
          <tr><td>Team Required</td><td>DevOps engineer ($120K+/yr)</td><td>None</td></tr>
          <tr><td>Monthly Cost (Gold / light–moderate)</td><td>$10,000+</td><td>~$375–500 (base + usage)</td></tr>
          <tr><td>Monthly Cost (Premium / heavy, 5K jobs)</td><td>$15,000+</td><td>~$750–900 (base + usage)</td></tr>
          <tr><td>Maintenance</td><td>20+ hr/week</td><td>0 hr (fully managed)</td></tr>
          <tr><td>Scaling</td><td>Manual</td><td>Automatic</td></tr>
          <tr><td>Cloud account required</td><td>Yes</td><td>No</td></tr>
        </tbody>
      </table>
      <Note>A Gold (dev) cluster starts at <strong>$375/month</strong> for always-on capacity, then usage. You do not need Premium pricing for development or staging.</Note>

      <H3>vs BYOC (Bring Your Own Cloud)</H3>
      <P>BYOC platforms deploy into <em>your</em> AWS/GCP/Azure account. You get isolation, but you also get a second bill, IAM setup, and ongoing cluster maintenance.</P>
      <table className="docs-table">
        <thead><tr><th>Feature</th><th>BYOC</th><th>Mermadic</th></tr></thead>
        <tbody>
          <tr><td>Setup Time</td><td>1–3 weeks</td><td>~1 hour</td></tr>
          <tr><td>Cloud account required</td><td>Yes (yours)</td><td>No</td></tr>
          <tr><td>Monthly bills</td><td>2 (platform + cloud)</td><td>1</td></tr>
          <tr><td>Est. all-in (light / Gold)</td><td>~$236* (excludes DevOps time)</td><td>~$420</td></tr>
          <tr><td>Est. all-in (heavy / Premium)</td><td>~$1,125*</td><td>~$780</td></tr>
          <tr><td>Infrastructure maintenance</td><td>3–8 hr/week</td><td>Zero</td></tr>
          <tr><td>Dedicated cluster + private VPC</td><td>Yes (in your account)</td><td>Yes (fully managed)</td></tr>
          <tr><td>Auto-scaling</td><td>Yes (adds to cloud bill)</td><td>Yes (included in usage)</td></tr>
          <tr><td>Disaster recovery</td><td>Limited / self-managed</td><td>Built-in from the dashboard</td></tr>
        </tbody>
      </table>
      <Note>* BYOC estimates are typical platform markup plus AWS EKS + EC2 + networking. They exclude cloud support plans and the engineering time to stand up and maintain the cluster.</Note>

      <H3>vs Serverless Functions</H3>
      <table className="docs-table">
        <thead><tr><th>Feature</th><th>Serverless</th><th>Mermadic</th></tr></thead>
        <tbody>
          <tr><td>Network Isolation</td><td>Shared multi-tenant network</td><td>Dedicated private VPC per account</td></tr>
          <tr><td>Compute Isolation</td><td>Shared multi-tenant</td><td>Dedicated isolated cluster</td></tr>
          <tr><td>Cost Model</td><td>Per-request (expensive at scale)</td><td>Base + usage (cheaper at scale)</td></tr>
          <tr><td>Cold Starts</td><td>Yes</td><td>No (nodes always warm)</td></tr>
          <tr><td>Noisy Neighbors</td><td>Yes (performance varies)</td><td>No (isolated cluster + isolated VPC)</td></tr>
          <tr><td>Private Registry</td><td>Requires separate setup</td><td>Included (MCR)</td></tr>
          <tr><td>CDCI Automation</td><td>Requires external CI</td><td>Built-in (GitHub push → redeploy)</td></tr>
          <tr><td>Custom Domains</td><td>Requires separate setup</td><td>Built-in (domain + cert management)</td></tr>
        </tbody>
      </table>
    </>
  ),

  'cluster-overview': (
    <>
      <H2>Cluster Overview</H2>
      <P>A cluster is a dedicated, isolated compute environment provisioned exclusively for your account — running inside a <strong>private VPC</strong> that belongs only to you. All job runs, deployments, container builds, and automation pipelines run inside your cluster and your private network. No resources or network paths are shared with other customers.</P>

      <UiAccess>
        <ul className="docs-list">
          <li>Click <strong><Link to="/cluster">Cluster</Link></strong> in the navbar to open Cluster Management.</li>
          <li>All your clusters are listed in the left sidebar with a live status dot.</li>
          <li>Click <strong>+ New</strong> to provision a new dedicated cluster.</li>
          <li>The active cluster context shown in the top-right of the navbar determines which cluster your jobs and deployments target — see <strong>Switching Clusters</strong> below.</li>
        </ul>
      </UiAccess>

      <H3>Tiers</H3>
      <table className="docs-table">
        <thead><tr><th>Tier</th><th>Fixed / month</th><th>Always-on nodes</th><th>Job-only nodes</th><th>Best for</th></tr></thead>
        <tbody>
          <tr><td>Shared</td><td>$0</td><td>Shared Mermadic cluster</td><td>—</td><td>Trying things out, low-volume jobs</td></tr>
          <tr><td>Gold</td><td>$375</td><td>2 medium, scales to 5</td><td>Up to 10 × 2 vCPU / 8 GB (scale to zero when idle)</td><td>Development, staging, small workloads</td></tr>
          <tr><td>Premium</td><td>$500</td><td>2 xlarge, scales to 6 + 0–2 burst 2xlarge</td><td>Up to 20 × 4 vCPU / 16 GB (scale to zero when idle)</td><td>Production traffic, higher reliability</td></tr>
        </tbody>
      </table>
      <Note><strong>Job-only nodes</strong> run batch / one-off jobs only. Each node is sized for that tier — <strong>Gold: 2 vCPU / 8 GB RAM</strong>, <strong>Premium: 4 vCPU / 16 GB RAM</strong>. They scale up when jobs need capacity and scale back to zero when the queue is idle — separate from the always-on nodes that host deployments and the control plane.</Note>
      <Note><strong>GPU jobs</strong> are a Premium-only option. Premium clusters also carry a GPU job pool (1× NVIDIA T4 per node) that idles at zero nodes and only spins up for jobs created with <IC>useGpu</IC>, billed at a flat per-GPU-hour rate (CPU and memory included) with a 15-minute minimum. Deployments and automations do not support GPU.</Note>

      <H3>What's Included</H3>
      <ul className="docs-list">
        <li><strong>Isolated private VPC</strong> — your clusters run in a private network that no other customer can reach</li>
        <li><strong>Isolated cluster</strong> — multi-AZ, auto-scaling, fully isolated compute on your own nodes</li>
        <li><strong>Dedicated API per cluster</strong> — each cluster exposes its own dedicated API endpoint</li>
        <li><strong>Private container registry</strong> — shared across your account; push images once, run them on any of your clusters with no credentials</li>
        <li><strong>Load balancer</strong> — HTTPS endpoints for every deployment, custom domain support</li>
        <li><strong>Monitoring dashboard</strong> — live pod health, replica counts, and usage balance</li>
        <li><strong>Deployment backups</strong> — configs and ConfigMaps are backed up and restored on recovery</li>
        <li><strong>Self-managed disaster recovery</strong> — trigger a full cluster rebuild from the dashboard at any time</li>
      </ul>

      <Note>The Shared tier supports job runs and image pushes only. Deployments require a Gold or Premium cluster.</Note>
    </>
  ),

  'cluster-switching': (
    <>
      <H2>Switching Clusters</H2>
      <P>If you have multiple clusters, you can switch which one the dashboard targets at any time using the cluster picker in the top-right of the navbar.</P>

      <UiAccess>
        <ul className="docs-list">
          <li>Look for the <strong>Cluster</strong> pill in the top-right corner of the navbar — it shows your currently active cluster name and tier badge.</li>
          <li>Click on it to open the cluster dropdown and select a different cluster.</li>
          <li>All subsequent actions — job submissions, deployments, registry operations — will target the newly selected cluster.</li>
        </ul>
      </UiAccess>

      <div className="docs-screenshot-wrapper">
        <img src={clusterPickerImg} alt="Cluster picker in the navbar" className="docs-screenshot" />
        <p className="docs-screenshot-caption">Cluster picker — top-right of the navbar. The pill shows the active cluster name and its tier badge.</p>
      </div>

      <div className="docs-screenshot-wrapper">
        <img src={clusterPickerDropdownImg} alt="Cluster picker dropdown open, showing the list of clusters" className="docs-screenshot" />
        <p className="docs-screenshot-caption">Click the pill to expand the dropdown, then select the cluster you want to target. A ✓ marks the active cluster.</p>
      </div>

      <H3>What Changes When You Switch</H3>
      <ul className="docs-list">
        <li><strong>Dedicated API</strong> — Jobs are submitted to the selected cluster's dedicated API.</li>
        <li><strong>Deployments</strong> — Deployments, ConfigMaps, and Domains show resources belonging to the selected cluster.</li>
        <li><strong>CDCI Automation</strong> — Automation pipelines are scoped to the selected cluster.</li>
      </ul>

      <H3>What Stays the Same</H3>
      <ul className="docs-list">
        <li><strong>Container Registry (MCR)</strong> — Your registry is shared across your whole account, not tied to a single cluster. The same images are available to every cluster you own, so switching clusters does not change what you see in Registry.</li>
      </ul>

      <Note>The selected cluster is stored in your browser. If you open the dashboard on a new browser or device, it will default to your first cluster.</Note>
    </>
  ),

  'cluster-create': (
    <>
      <H2>Creating a Cluster</H2>

      <UiAccess>
        <ul className="docs-list">
          <li>Click <strong><Link to="/cluster">Cluster</Link></strong> in the navbar.</li>
          <li>Click <strong>+ New</strong> in the left sidebar to open the creation wizard.</li>
          <li>Follow the steps below to configure, pay, and provision.</li>
        </ul>
      </UiAccess>

      <P>The wizard walks through three steps:</P>

      <H3>Step 1 — Configure</H3>
      <ul className="docs-list">
        <li><strong>Cluster Name</strong> — Lowercase letters, numbers, and hyphens only (e.g. <IC>my-app</IC>). This becomes part of your default deployment URLs.</li>
        <li><strong>Tier</strong> — Select Gold or Premium. The monthly price is shown on each card.</li>
        <li>Click <strong>Continue to Payment</strong>. The cluster is saved as <IC>pending</IC> while you complete payment.</li>
      </ul>

      <H3>Step 2 — Payment</H3>
      <ul className="docs-list">
        <li>You are redirected to Stripe Checkout to set up the monthly subscription.</li>
        <li>The first month is charged immediately. Your card is saved as the default payment method for this cluster's usage charges going forward.</li>
        <li>Once payment succeeds, the cluster status changes to <IC>approved</IC> and you are returned to the dashboard.</li>
      </ul>

      <H3>Step 3 — Provision</H3>
      <ul className="docs-list">
        <li>With the cluster in <IC>approved</IC> status, click <strong>▶ Start Cluster</strong>.</li>
        <li>Status moves to <IC>building</IC>. Provisioning typically takes <strong>10–20 minutes</strong>.</li>
        <li>Once complete, status becomes <IC>active</IC> and the cluster is ready to use.</li>
      </ul>

      <Note>If you close the browser after requesting but before completing payment, the cluster stays <IC>pending</IC>. Open it from the sidebar and click <strong>Complete Payment</strong> to resume.</Note>
    </>
  ),

  'cluster-status': (
    <>
      <H2>Cluster Status Reference</H2>

      <UiAccess>
        <ul className="docs-list">
          <li>Click <strong><Link to="/cluster">Cluster</Link></strong> in the navbar.</li>
          <li>Each cluster in the sidebar shows a colored status dot reflecting its current lifecycle state.</li>
          <li>Click a cluster to see its details and available actions (Start, Recover, Delete, etc.).</li>
        </ul>
      </UiAccess>

      <P>The status dot in the sidebar reflects the current lifecycle state of each cluster.</P>

      <table className="docs-table">
        <thead><tr><th>Status</th><th>Meaning</th><th>Next action</th></tr></thead>
        <tbody>
          <tr><td><IC>pending</IC></td><td>Requested but payment not yet completed</td><td>Click <strong>Complete Payment</strong></td></tr>
          <tr><td><IC>approved</IC></td><td>Payment confirmed, provisioning not yet started</td><td>Click <strong>▶ Start Cluster</strong></td></tr>
          <tr><td><IC>building</IC></td><td>Infrastructure being provisioned (~10–20 min)</td><td>Wait — page auto-refreshes every 60 s</td></tr>
          <tr><td><IC>active</IC></td><td>Cluster is live and accepting workloads</td><td>—</td></tr>
          <tr><td><IC>build_failed</IC></td><td>Provisioning failed</td><td>Click <strong>↻ Retry Build</strong> or contact support</td></tr>
          <tr><td><IC>recovering</IC></td><td>Cluster is being destroyed and rebuilt</td><td>Wait ~1 hour; email sent on completion</td></tr>
          <tr><td><IC>deleting</IC></td><td>Teardown in progress</td><td>Wait</td></tr>
        </tbody>
      </table>

      <P>Clusters in <IC>building</IC>, <IC>recovering</IC>, or <IC>deleting</IC> states are auto-refreshed every 60 seconds in the dashboard.</P>
    </>
  ),

  'cluster-billing': (
    <>
      <H2>Cluster Billing</H2>

      <UiAccess>
        <ul className="docs-list">
          <li>Go to <strong><Link to="/account">My Account</Link></strong> → <strong>Payment History</strong> to view all charges.</li>
          <li>Go to <strong>My Account → Payment Methods</strong> to update your saved card.</li>
          <li>Your current usage balance and subscription details are shown inside the cluster card at <strong><Link to="/cluster">Cluster</Link></strong>.</li>
        </ul>
      </UiAccess>

      <H3>Fixed Monthly Subscription</H3>
      <P>Gold and Premium clusters are billed monthly via Stripe. The subscription is created during cluster setup and renews automatically on the same day each month. The fixed fee covers the always-on nodes, load balancer, and networking.</P>

      <H3>Job Run Usage</H3>
      <P>Usage from job executions accrues on top of the fixed fee. All tiers pay the same base rates:</P>
      <table className="docs-table">
        <thead><tr><th>Component</th><th>Rate</th></tr></thead>
        <tbody>
          <tr><td>Per job execution</td><td>$0.02</td></tr>
          <tr><td>Per vCPU-hour</td><td>$0.04</td></tr>
          <tr><td>Per GB-hour memory</td><td>$0.03</td></tr>
        </tbody>
      </table>
      <Note>Shared tier applies a ~15% premium on all job usage rates.</Note>

      <H3>Deployment Usage</H3>
      <P>Long-running deployments (Gold and Premium only) are metered hourly by replica count:</P>
      <table className="docs-table">
        <thead><tr><th>Component</th><th>Rate</th></tr></thead>
        <tbody>
          <tr><td>Per deployment instance-hour</td><td>$0.008</td></tr>
        </tbody>
      </table>

      <H3>Job Billing — Incremental Hourly Metering</H3>
      <P>Job compute costs are metered <strong>incrementally</strong> as the job runs, not as a single charge at the end:</P>
      <ul className="docs-list">
        <li>The per-job-execution flat fee (<IC>$0.02</IC>) is recorded when the job starts.</li>
        <li>vCPU-hour and GB-hour costs are recorded <strong>once per hour</strong> while the job is running.</li>
        <li>When the job finishes, a final partial-hour charge is recorded for any remaining fractional hour.</li>
        <li>If a running job is deleted, billing is finalized at the moment the container stops.</li>
      </ul>

      <H3>How Usage Charges Work</H3>
      <P>Each cluster tracks a running <IC>outstanding_balance</IC>. After every job or metering tick, the cost is added to the balance. When the balance reaches <strong>$100</strong>, your saved payment method is charged automatically and the balance resets. If the charge fails, the cluster is marked <IC>past_due</IC> and new workloads are blocked until the payment method is updated.</P>

      <H3>Cancellation</H3>
      <P>Cancel by deleting the cluster from the dashboard. This triggers a teardown of the infrastructure. Outstanding usage balance is charged at the time of cancellation.</P>
    </>
  ),

  'cluster-recovery': (
    <>
      <H2>Cluster Recovery</H2>
      <P>If your cluster enters an unrecoverable state — or you need a clean slate — you can trigger a full rebuild from the dashboard without contacting support.</P>

      <UiAccess>
        <ul className="docs-list">
          <li>Click <strong><Link to="/cluster">Cluster</Link></strong> in the navbar and select your cluster from the sidebar.</li>
          <li>Click <strong>↺ Recover</strong> in the cluster detail header (only visible when status is <IC>active</IC>).</li>
          <li>Review the list of deployments and ConfigMaps that will be restored, then click <strong>↺ Start Rebuilding</strong>.</li>
          <li>An email is sent when recovery completes (~1 hour).</li>
        </ul>
      </UiAccess>

      <H3>What Recovery Does</H3>
      <ul className="docs-list">
        <li>Destroys the existing cluster infrastructure completely.</li>
        <li>Provisions a brand-new cluster from scratch using the same name and tier.</li>
        <li>Automatically restores all <strong>ConfigMaps</strong> and <strong>deployment configs</strong> that were backed up.</li>
        <li>Sends an <strong>email notification</strong> when recovery is complete.</li>
      </ul>

      <H3>How to Trigger Recovery</H3>
      <ul className="docs-list">
        <li>Open the cluster in the sidebar (must be <IC>active</IC> status).</li>
        <li>Click <strong>↺ Recover</strong> in the cluster header.</li>
        <li>Review the summary of deployments and ConfigMaps that will be restored.</li>
        <li>Click <strong>↺ Start Rebuilding</strong> to confirm.</li>
      </ul>

      <H3>What to Expect</H3>
      <table className="docs-table">
        <thead><tr><th>Phase</th><th>Duration</th></tr></thead>
        <tbody>
          <tr><td>Teardown of old cluster</td><td>~10 min</td></tr>
          <tr><td>Provisioning new cluster</td><td>~10–15 min</td></tr>
          <tr><td>Restoring deployments &amp; ConfigMaps</td><td>~5 min</td></tr>
          <tr><td><strong>Total downtime</strong></td><td><strong>~1 hour</strong></td></tr>
        </tbody>
      </table>

      <Note>Recovery is a destructive operation. The cluster will be completely offline during the process. Plan accordingly for production workloads.</Note>
    </>
  ),

  'api-auth': (
    <>
      <H2>API Reference — Authentication</H2>
      <P>All Mermadic API requests use API key authentication. Generate a key from <strong>My Account → Developer Access</strong> and pass it as a Bearer token on every request.</P>

      <Note>If you have a dedicated cluster, your dedicated API has its own base URL. Visit <strong>My Account → API docs</strong> to find your cluster-specific endpoint and explore the full interactive API reference.</Note>

      <H3>Request Header</H3>
      <Code lang="bash">{`Authorization: Bearer YOUR_API_KEY`}</Code>

      <H3>Generating an API Key</H3>
      <ul className="docs-list">
        <li>Log in and click <strong>My Account</strong> in the top-right.</li>
        <li>Select <strong>Developer Access</strong> in the left sidebar.</li>
        <li>Enter a descriptive key name (e.g. <IC>production-pipeline</IC>) and click <strong>Generate Key</strong>.</li>
        <li>Copy the key immediately — it is displayed only once.</li>
      </ul>
      <div className="docs-screenshot-wrapper">
        <img src={developerAccessImg} alt="Developer Access — generate API key" className="docs-screenshot" />
        <p className="docs-screenshot-caption">My Account → Developer Access</p>
      </div>

      <Note>API keys are scoped to your account and have full access. Treat them like passwords. Revoke and rotate keys from the Developer Access page at any time.</Note>
    </>
  ),

  'api-jobs': (
    <>
      <H2>API Reference — Dedicated API</H2>

      <H3>Job Creation</H3>
      <div className="docs-endpoint-list">
        <EndpointRow method="POST" path="/job-runner/v1/dockerhub/create-job" desc="DockerHub images (public or private)" />
        <EndpointRow method="POST" path="/job-runner/v1/ecr/create-job"       desc="AWS Elastic Container Registry" />
        <EndpointRow method="POST" path="/job-runner/v1/acr/create-job"       desc="Azure Container Registry" />
        <EndpointRow method="POST" path="/job-runner/v1/mcr/create-job"       desc="Mermadic Container Registry (no credentials needed)" />
      </div>

      <H3>Job Utilities</H3>
      <div className="docs-endpoint-list">
        <EndpointRow method="POST" path="/job-runner/v1/get-log?job_id=..."        desc="Retrieve logs for a job — job_id is a query parameter" />
        <EndpointRow method="GET"  path="/job-runner/v1/download-log?job_id=..."   desc="Download the complete log as a text file — works for cron runs too" />
        <EndpointRow method="POST" path="/job-runner/v1/get-job-detail?job_id=..." desc="Get full job details — job_id is a query parameter" />
        <EndpointRow method="POST" path="/job-runner/v1/all-logs"                  desc="List jobs with metadata (id, image, status, time) — optional JSON filter body" />
        <EndpointRow method="POST" path="/job-runner/v1/delete-job?job_id=..."     desc="Stop a running job (running jobs only — returns 400 for finished jobs)" />
      </div>

      <H3>Request Body</H3>
      <P>Each <IC>create-job</IC> endpoint returns the new job id as a JSON string (HTTP 201). Authenticate with your API key in the <IC>Authorization: Bearer</IC> header — never in the body.</P>
      <Code lang="json">{`{
  "image": "string",               // Registry-qualified image name
  "commandArgs": ["string"],       // Command + arguments array
  "resourceRequests": {
    "cpu": 0.5,                    // vCPU cores
    "memory": 1.0                  // Memory in GB
  },
  "credential": {                  // Omit for public / MCR images
    "dockerhubUsername": "...",
    "dockerhubPassword": "...",
    "awsAccessKeyId": "...",
    "awsSecretAccessKey": "...",
    "regionName": "us-east-1",
    "azureUsername": "...",
    "azurePassword": "..."
  },
  "env": [                         // Optional environment variables
    { "name": "KEY", "value": "VALUE" }
  ],
  "configmapName": "my-env",       // Optional — works on every tier
  "useGpu": false                  // Optional — Premium only (1× NVIDIA T4)
}`}</Code>
    </>
  ),

  'api-registry': (
    <>
      <H2>API Reference — Container Registry</H2>

      <P>Images come in two types: <strong>Job</strong> (<IC>image_type=short</IC>, default) and <strong>Deployment</strong> (<IC>image_type=long</IC>). Listing and removal endpoints act on the type you pass.</P>

      <H3>Registry Endpoints</H3>
      <div className="docs-endpoint-list">
        <EndpointRow method="GET"  path="/v1/cdci/mcr/images?image_type=short" desc="List images of a type (short = Job, long = Deployment)" />
        <EndpointRow method="POST" path="/v1/cdci/push-build"                  desc="Build from a ZIP and push to MCR (multipart)" />
        <EndpointRow method="POST" path="/v1/cdci/remove-mcr-image"            desc="Remove an image tag (multipart: image_name, image_type)" />
        <EndpointRow method="GET"  path="/v1/cdci/builds"                      desc="List your builds with status and timings" />
        <EndpointRow method="GET"  path="/v1/cdci/build-log/{build_id}"        desc="Retrieve a build's log" />
        <EndpointRow method="GET"  path="/v1/cdci/build-log/{build_id}/download" desc="Download the complete build log as a text file" />
        <EndpointRow method="POST" path="/v1/cdci/cancel-build/{build_id}"     desc="Cancel a running build" />
        <EndpointRow method="GET"  path="/v1/cdci/ci-log/{image_name}"         desc="Retrieve the latest build log for an image" />
        <EndpointRow method="GET"  path="/v1/cdci/list-all-build-push"         desc="List image names you have built" />
      </div>

      <H3>Build & Push Form Fields</H3>
      <table className="docs-table">
        <thead><tr><th>Field</th><th>Type</th><th>Required</th><th>Description</th></tr></thead>
        <tbody>
          <tr><td><IC>zip_file</IC></td><td>file</td><td>Yes</td><td>ZIP archive with Dockerfile at root</td></tr>
          <tr><td><IC>image_name</IC></td><td>string</td><td>Yes</td><td>Plain name — spaces and unsupported characters removed automatically</td></tr>
          <tr><td><IC>image_type</IC></td><td>string</td><td>No</td><td><IC>short</IC> (Job, default) or <IC>long</IC> (Deployment)</td></tr>
          <tr><td><IC>app_name</IC></td><td>string</td><td>No</td><td>Triggers auto-redeploy of this deployment on a successful build</td></tr>
        </tbody>
      </table>

      <H3>Example</H3>
      <Code lang="bash">{`curl -X POST https://job.mermadicrunner.com/v1/cdci/push-build \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -F "zip_file=@./my-project.zip" \\
  -F "image_name=My Processor" \\
  -F "image_type=short"

# Response (HTTP 201)
{ "pushedImage": "MyProcessor" }`}</Code>
    </>
  ),

  'api-deployments': (
    <>
      <H2>API Reference — Deployments & ConfigMaps</H2>

      <H3>ConfigMap Endpoints</H3>
      <div className="docs-endpoint-list">
        <EndpointRow method="POST"   path="/v1/deployment/create-configmap"           desc="Create ConfigMap from JSON key-value pairs" />
        <EndpointRow method="POST"   path="/v1/deployment/create-configmap-from-file" desc="Create ConfigMap from a .env file upload" />
        <EndpointRow method="GET"    path="/v1/deployment/list-configmap"             desc="List all ConfigMap names" />
        <EndpointRow method="GET"    path="/v1/deployment/view-configmap?name=..."    desc="View ConfigMap key-value data" />
        <EndpointRow method="DELETE" path="/v1/deployment/delete-configmap?name=..."  desc="Delete a ConfigMap (blocked if used by a deployment)" />
      </div>

      <H3>Deployment Endpoints</H3>
      <div className="docs-endpoint-list">
        <EndpointRow method="POST"   path="/v1/deployment/create-deployment-config"  desc="Create or update a deployment config" />
        <EndpointRow method="GET"    path="/v1/deployment/list-deployments"          desc="List all deployment configs" />
        <EndpointRow method="POST"   path="/v1/deployment/deploy"                   desc="Deploy or redeploy an app" />
        <EndpointRow method="POST"   path="/v1/deployment/stop-deployment"          desc="Stop a running deployment (keeps config)" />
        <EndpointRow method="DELETE" path="/v1/deployment/delete-deployment"        desc="Delete a deployment config and stop the app" />
        <EndpointRow method="GET"    path="/v1/deployment/get-deployment-status"    desc="Get live pod status for a deployment" />
      </div>
    </>
  ),

  'api-domains': (
    <>
      <H2>API Reference — Domains</H2>

      <H3>Domain Endpoints</H3>
      <div className="docs-endpoint-list">
        <EndpointRow method="POST"   path="/v1/domain/add"      desc="Register a new domain and request a certificate" />
        <EndpointRow method="GET"    path="/v1/domain/list"     desc="List all domains and their verification status" />
        <EndpointRow method="GET"    path="/v1/domain/verified" desc="List only verified domains" />
        <EndpointRow method="GET"    path="/v1/domain/check"    desc="Poll unverified domains and update their status" />
        <EndpointRow method="DELETE" path="/v1/domain/delete"   desc="Remove a domain and delete its certificate" />
      </div>

      <H3>Example — Add a Domain</H3>
      <Code lang="bash">{`curl -X POST https://job.mermadicrunner.com/v1/domain/add \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"domain": "example.com"}'

# Response — contains the CNAME record to add to your DNS provider
{
  "domain": "example.com",
  "cname_name": "_acme-challenge.example.com",
  "cname_value": "xyz.acm.aws.com",
  "status": "pending"
}`}</Code>
    </>
  ),

  faq: (
    <>
      <H2>FAQ</H2>
      {[
        ['How do I pass arguments to my job?', <>Use the <IC>commandArgs</IC> array. Your script handles parsing (e.g. Python's <IC>argparse</IC>, Node's <IC>process.argv</IC>).</>],
        ['How do I authenticate with the API?', <>Generate an API key from <strong>My Account → Developer Access</strong>, then include it as <IC>Authorization: Bearer YOUR_API_KEY</IC> on every request. See the Quick Start for step-by-step instructions.</>],
        ['Where can I see job logs?', <>Use <IC>/job-runner/v1/get-log</IC> with your <IC>job_id</IC>, or <IC>/job-runner/v1/download-log</IC> for the complete log as a file. Logs are retained for 30 days. You can also view them in the dashboard under <strong>Workloads → Jobs</strong> (and <strong>Workloads → Cron</strong> for scheduled runs) — select a job in the left pane to see its log, with a button to download the full log.</>],
        ['What registries are supported?', <>DockerHub (public and private), private cloud registries (with credentials), and the built-in Mermadic Container Registry (MCR — no credentials needed).</>],
        ['Do I need credentials for MCR images?', <>No. MCR images are pre-authorized on your dedicated cluster. Set the <IC>image</IC> field to your image name — no <IC>credential</IC> block is required.</>],
        ['What happens if a job fails?', <>Check your logs via the dashboard or API. Failed jobs (non-zero exit code) do not incur the per-job charge, but compute usage during the attempt is still billed.</>],
        ['Can I run recurring jobs?', <>Yes — use the built-in <strong>Cron</strong> feature (<strong>Workloads → Cron</strong>) to schedule a container to run on a recurring cron schedule right inside your cluster. Cron Jobs accept MCR images only, so no registry credentials are ever stored. You can also still trigger jobs externally from any HTTP client, Airflow, Prefect, etc.</>],
        ['How long can jobs run?', <>Jobs are hard-capped at <strong>24 hours</strong> (<IC>activeDeadlineSeconds</IC>). Contact support if you need a longer window.</>],
        ['Is a ConfigMap required for deployments?', <>No. ConfigMaps are optional. Only add one if your app reads environment variables at startup.</>],
        ['Can I delete a ConfigMap that is in use?', <>No. The platform blocks deletion if any active deployment references the ConfigMap. Remove the reference in Deployments first.</>],
        ['How does Automation (CDCI) work?', <>Connect a GitHub branch to an app. Every push triggers a build, pushes the new image to MCR, and — if Auto Deploy is on — redeploys your service automatically.</>],
        ['What if the first build after activating Automation fails?', <>The Activate Automation button stays visible on the review step. Fix your repository and click it again to retry without recreating the automation.</>],
        ['How do I use my own domain?', <>Go to Apps → Domains, add your domain, add the CNAME record to your DNS provider, and wait for verification. Once verified, select the domain in Deployments as the custom host for any app.</>],
        ['How long does domain verification take?', <>Usually 5–30 minutes after you add the CNAME record to your DNS provider. Click ↻ Check Status to refresh.</>],
        ['Is my data isolated from other customers?', <>Yes — at the network level. Every dedicated account gets its own private AWS VPC. Your clusters run in subnets no other customer can reach. On top of that, compute is dedicated and the API endpoint is your own. Data is encrypted at rest and in transit.</>],
        ['What is a dedicated private VPC?', <>A Virtual Private Cloud (VPC) is a private, logically isolated section of the AWS network. Your clusters live inside this VPC, meaning no other customer\'s traffic can reach your subnets — not even on the same physical hardware. Most platforms isolate pods; Mermadic isolates the entire network.</>],
        ['Do all my clusters share the same VPC?', <>Yes. All your dedicated clusters (Gold and Premium) share one private VPC, which keeps inter-cluster traffic fast. When you delete your last cluster, Mermadic automatically tears down the VPC too.</>],
        ["Is the infrastructure in Mermadic's cloud account?", <>Yes. We provision dedicated infrastructure for each customer in our managed environment. Enterprise customers requiring hosting in their own cloud account can contact us for a custom option.</>],
        ['Can I cancel anytime?', <>Yes. Month-to-month plans with 30-day notice. Annual plans (17% discount) require 90-day notice.</>],
      ].map(([q, a]) => (
        <div key={q} className="docs-faq-item">
          <H4>{q}</H4>
          <p className="docs-p">{a}</p>
        </div>
      ))}
    </>
  ),
};

/* ─────────────────────────────────────────────
   Main component
───────────────────────────────────────────── */
export default function Documentation() {
  const location = useLocation();
  const navigate = useNavigate();
  const contentRef = useRef(null);

  /** The URL hash is the single source of truth, so sections can be linked to
      directly (e.g. /documentation#deploy-secrets) and shared. */
  const hashId = decodeURIComponent(location.hash.replace(/^#/, ''));
  const activeId = Object.prototype.hasOwnProperty.call(SECTIONS, hashId) ? hashId : 'overview';

  useEffect(() => {
    Prism.highlightAll();
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [activeId]);

    return (
    <div className="docs-page">
      {/* ── Sidebar ── */}
      <nav className="docs-sidebar">
        <div className="docs-sidebar-inner">
          {NAV.map(({ group, items }) => (
            <div key={group} className="docs-nav-group">
              <span className="docs-nav-group-label">{group}</span>
              {items.map(({ id, label }) => (
                <button
                  key={id}
                  className={`docs-nav-item ${activeId === id ? 'docs-nav-item--active' : ''}`}
                  onClick={() => navigate(`/documentation#${id}`)}
                >
                  {label}
                </button>
                ))}
            </div>
          ))}
        </div>
      </nav>

      {/* ── Content ── */}
      <main className="docs-content" ref={contentRef}>
        <div className="docs-content-inner">
          {SECTIONS[activeId]}
        </div>
      </main>
    </div>
  );
}
